// Shared types between frontend and backend
// Re-export types that are used by both applications

// PESEL parser/validator — the single implementation shared by the backend
// (apps/backend/src/common/utils/pesel.util.ts) and the employee form.
export * from './pesel';
import type { Sex } from './pesel';

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  groups: string[];
  isAdmin: boolean;
  allowedCompanies: string[]; // NIP numbers user has access to
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  /** HTTP status code of a failed response (set by the frontend api client). */
  status?: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Tag types
export const TAG_MODULES = ['expense', 'sales', 'contractors'] as const;
export type TagModule = (typeof TAG_MODULES)[number];

export interface Tag {
  id: string;
  companyNip: string;
  module: TagModule;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTagDto {
  module: TagModule;
  name: string;
  color: string;
}

export interface UpdateTagDto {
  name?: string;
  color?: string;
}

// Company types for sidebar
/** Tone of a company logo's artwork; decides the plate the UI frames it on. */
export type LogoInk = 'light' | 'dark';

export interface CompanyListItem {
  nip: string;
  name: string;
  invoiceCount: number;
  monthlyInvoiceCount: number;
  contractorCount: number;
  /** Pre-signed S3 URL of the company logo, or null when none is set.
   *  Included in the list so the sidebar can brand the switcher without an
   *  extra round-trip per company. */
  logoUrl?: string | null;
  /** Tone of the logo ARTWORK (not of the theme): 'light' = light ink (e.g. the
   *  white FSS logo) → frame it on a dark plate; 'dark' = dark ink → frame it on
   *  a white plate. Logos are transparent, so no single plate suits both. */
  logoInk?: LogoInk | null;
}

// Contractor types.
// Since the sales-contractors rework (2026-07) a contractor is a GENERIC
// counterparty: supplier (vendor on purchase invoices), customer (buyer on
// KSeF sale invoices) or both. The role is DERIVED from invoice stats, never
// stored.
export type ContractorRole = 'supplier' | 'customer' | 'both' | 'none';

export interface ContractorListItem {
  id: string;
  name: string | null;
  nip: string | null;
  // Accounting-program / ERP identifier — manually maintained, reserved as
  // the join key for a future ERP integration.
  erpId: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  bankAccounts: string[];
  structuredBankAccounts: ContractorBankAccountData[];
  /** Government-registry verification (White List / VIES), if ever performed. */
  vatStatus: string | null;
  registrySource: string | null;
  registryVerifiedAt: string | null;
  tags: string[]; // Tag IDs
  // Money and counters, BOTH directions: `null` = the caller has neither the
  // 'expense' nor the 'sales' module (TURNOVER_MODULES) — turnover is invoice
  // data, and the 'contractors' module alone (e.g. the 'crm' role since
  // 2026-09-10) does not open it. `role` is NOT nulled: it is a directory
  // fact, not an amount.
  // Purchase side (this counterparty as OUR SUPPLIER) — historical names
  // kept for compatibility; amounts are PLN-normalized net.
  invoiceCount: number | null;
  yearlyTurnoverNet: number | null;
  monthlyTurnoverNet: number | null;
  lastInvoiceDate: string | null;
  // Sales side (this counterparty as OUR CUSTOMER).
  salesInvoiceCount: number | null;
  salesYearlyTurnoverNet: number | null;
  salesMonthlyTurnoverNet: number | null;
  lastSaleDate: string | null;
  role: ContractorRole;
  isByCar: boolean;
}

export interface ContractorDetail extends ContractorListItem {
  defaultInvoiceTagIds: string[];
}

export interface UpdateContractorDto {
  name?: string | null;
  nip?: string | null;
  erpId?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  bankAccounts?: string[];
  structuredBankAccounts?: ContractorBankAccountData[];
  tags?: string[];
  isByCar?: boolean;
  defaultInvoiceTagIds?: string[];
}

/** Contractor document archive types (spec 2026-07-16 §3.1). */
export const CONTRACTOR_DOC_TYPES = [
  'nda', 'umowa', 'aneks', 'pelnomocnictwo', 'oferta', 'inne',
  'wz', 'wz_zalacznik',
] as const;
export type ContractorDocType = (typeof CONTRACTOR_DOC_TYPES)[number];

/**
 * Warehouse documents — a WZ and its attachments carry no expiry semantics
 * (their date is a delivery date, not a validity date), so the
 * contractor-document reminder cron skips them.
 */
export const NON_EXPIRING_CONTRACTOR_DOC_TYPES = [
  'wz', 'wz_zalacznik',
] as const satisfies readonly ContractorDocType[];

/** API shape — deliberately no s3Key (downloads via presigned-URL endpoint). */
export interface ContractorDocument {
  id: string;
  contractorId: string;
  docType: ContractorDocType;
  label: string | null;
  fileName: string;
  documentDate: string | null;
  expiryDate: string | null;
  note: string | null;
  uploadedAt: string;
  returnedAt: string | null;
  returnConfirmed: boolean;
  hasReturnedFile: boolean;
  /** Digital-signature check of the RETURNED scan; null = not verified yet. */
  signatureVerification?: SignatureVerification | null;
}

/**
 * Metadata patch of an existing contractor document
 * (PATCH :nip/contractors/:contractorId/documents/:docId). PUT-as-patch: a
 * key that is NOT sent keeps its stored value, a key sent as `null` clears
 * it. Changing `expiryDate` restarts the expiry-reminder ladder server-side.
 */
export interface UpdateContractorDocumentDto {
  label?: string | null;
  documentDate?: string | null;
  expiryDate?: string | null;
  note?: string | null;
}

/**
 * Digital signatures embedded in a returned PDF (PAdES): Polish qualified
 * signatures (Certum, Szafir, Sigillum, EuroCert, Cencert…) and podpis
 * zaufany from podpis.gov.pl. Detached XAdES (.xml) files are out of scope —
 * the return pipeline only carries PDFs.
 */
export type PdfSignatureKind = 'qualified' | 'trusted_profile' | 'other';

export interface PdfSignatureInfo {
  /** From the signing certificate subject (givenName+surname, else CN). */
  signerName: string | null;
  /** Cert subject serialNumber with any embedded PESEL/ID digits masked
   *  (e.g. "PNOPL-84**********") — never expose the full number. */
  signerIdentifier: string | null;
  /** Issuing CA common name. */
  issuer: string | null;
  /** Claimed signing time (CMS signingTime attr, else the signature dictionary's
   *  /M) — informational, not proven. */
  signedAt: string | null;
  /**
   * `/Name` from the PDF signature dictionary. For podpis zaufany the
   * certificate identifies the ministry SEAL, not the person — the human is
   * named only here, so this is what to show and match on. Claimed by the
   * signing tool, not cryptographically proven; absent on older records.
   */
  declaredSignerName?: string | null;
  /** valid = document bytes match the signature; invalid = tampered/mismatch;
   *  unverifiable = unsupported algorithm or malformed structure. */
  integrity: 'valid' | 'invalid' | 'unverifiable';
  /** partial = the file has content appended AFTER the signed byte range
   *  (later edits or additional signatures) — signed content is still intact. */
  coverage: 'whole_document' | 'partial' | null;
  kind: PdfSignatureKind;
}

export interface SignatureVerification {
  verifiedAt: string;
  /** Empty = no embedded digital signature found (plain paper scan). */
  signatures: PdfSignatureInfo[];
}

/**
 * Dry run of attach-signed (`?dryRun=1`): what applying this file to a
 * completed acceptance WOULD add — shown to the operator before the pointer
 * moves, so they confirm a named signer, not a blind upload. Only returned
 * for a file that would be applied (a duplicate is 409, a refused one 422,
 * exactly as on the real call).
 */
export interface SignedRevisionPreview {
  /** Classifier reason, e.g. 'newer_revision_with_added_signature'. */
  reason: string;
  /** Signatures present in the uploaded file that the stored file lacks. */
  addedSignatures: PdfSignatureInfo[];
}

// Payment type detection
export type PaymentType = 'domestic' | 'sepa' | 'swift';
export type ChargeBearerCode = 'SHAR' | 'DEBT' | 'CRED';

// SEPA zone countries (excludes GB post-Brexit)
export const SEPA_COUNTRIES: ReadonlySet<string> = new Set([
  'AD','AT','BE','BG','CH','CY','CZ','DE','DK','EE','ES','FI','FR',
  'GR','HR','HU','IE','IS','IT','LI','LT','LU','LV','MC','ME','MK',
  'MT','NL','NO','PL','PT','RO','SE','SI','SK','SM','VA','XK',
]);

// Structured contractor bank account
export interface ContractorBankAccountData {
  id?: string; // present when reading from DB, omitted when creating
  iban: string;
  swiftBic?: string | null;
  currency?: string | null;
  label?: string | null;
}

// Company bank account
export interface CompanyBankAccount {
  iban: string;
  currency: string;
  name?: string; // Optional account name/description
  swiftBic?: string; // SWIFT/BIC code
}

// Company representation (umowa spec 2026-07-23): how many representatives
// must sign a generated contract on the company's behalf.
export const REPRESENTATION_MODES = ['one_person', 'two_person'] as const;
export type RepresentationMode = (typeof REPRESENTATION_MODES)[number];

// Company representative (board member / proxy) — the picker's source at
// contract generation time. Saved replace-all like bank accounts, so ids are
// NOT stable across saves; documents snapshot fullName/title at publication.
export interface CompanyRepresentative {
  id?: string; // present when reading from DB, omitted when creating
  fullName: string;
  title: string; // e.g. 'Prezes Zarządu' | 'Członek Zarządu' | 'Prokurent'
  /** Optional user account of the representative (countersign notifications
   *  + permission to sign). */
  userId?: string | null;
  sortOrder?: number;
}

// Company detail (full data for editing)
export interface CompanyDetail {
  nip: string;
  name: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  bankAccounts: CompanyBankAccount[];
  /** E-mail domains this company owns (lowercase, e.g. 'fss.cc'). Entra users
   *  with a work address in one of these domains automatically get an
   *  Employee record in this company. A domain maps to exactly one company. */
  emailDomains: string[];
  /** Pre-signed S3 URL of the company logo, or null when none is set. */
  logoUrl?: string | null;
  /** Tone of the logo artwork — see CompanyListItem.logoInk. */
  logoInk?: LogoInk | null;
  /** Registry/contact data used as document-template variables. */
  bdoNumber: string | null;
  /** Registry/contact data used as document-template variables. */
  krsNumber: string | null;
  /** Registry/contact data used as document-template variables. */
  contactEmail: string | null;
  /** Registry/contact data used as document-template variables. */
  website: string | null;
  /** Contract representation mode; null = not configured (contract
   *  publication for umowa templates is refused until an admin sets it). */
  representationMode: RepresentationMode | null;
  representatives: CompanyRepresentative[];
}

// Update company DTO
export interface UpdateCompanyDto {
  name?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  bankAccounts?: CompanyBankAccount[];
  emailDomains?: string[];
  bdoNumber?: string | null;
  krsNumber?: string | null;
  contactEmail?: string | null;
  website?: string | null;
  representationMode?: RepresentationMode | null;
  representatives?: CompanyRepresentative[];
}

// Create company DTO (admin-only)
export interface CreateCompanyDto {
  nip: string;
  name?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
}

// Delete company (admin-only)
export type DeleteCompanyMode = 'purge' | 'unassign';

export interface DeleteCompanyResult {
  invoices: number;
  contractors: number;
  tags: number;
  employees: number;
  vehicles: number;
  /** CRM is deleted in BOTH modes — it has no 'other' waiting room. */
  crmAccounts: number;
  crmLeads: number;
  /** pap_ tokens that were bound to the company; they stay, unbound (companyNip = null). */
  apiApplicationsDetached: number;
  s3ObjectsDeleted: number;
  /** S3 folder prefixes whose deleteByPrefix was skipped because one or more file
   *  moves into that folder failed (unassign mode only). Left as leftover objects
   *  awaiting manual reconciliation. Absent/empty when nothing was skipped. */
  s3Skipped?: string[];
}

// Payment methods (KSeF 2.0 standard + compensation + KSeF "PlatnoscInna" common cases)
export const PAYMENT_METHODS = [
  { value: 'bank',         label: 'Bank transfer',  labelPl: 'Przelew' },
  { value: 'card',         label: 'Card',           labelPl: 'Karta' },
  { value: 'cash',         label: 'Cash',           labelPl: 'Gotówka' },
  { value: 'mobile',       label: 'Mobile',         labelPl: 'Mobilna' },
  { value: 'compensation', label: 'Compensation',   labelPl: 'Kompensata' },
  { value: 'credit',       label: 'Credit',         labelPl: 'Kredyt' },
  { value: 'voucher',      label: 'Voucher',        labelPl: 'Bon' },
  { value: 'check',        label: 'Check',          labelPl: 'Czek' },
  { value: 'cod',          label: 'Cash on delivery', labelPl: 'Za pobraniem' },
  { value: 'direct_debit', label: 'Direct debit',   labelPl: 'Polecenie zapłaty' },
] as const;
export type PaymentMethodValue = typeof PAYMENT_METHODS[number]['value'];
export const PAYMENT_METHOD_VALUES: string[] = PAYMENT_METHODS.map(m => m.value);

/**
 * Coerce whatever an extractor produced into the enum, or null.
 *
 * The list above was only ever a SUGGESTION in the AI prompt and a best-effort
 * mapping in the KSeF parser, never a constraint on the write: production holds
 * `Allegro Finance`, `przedpłata`, `opłacono online` and `Płatność
 * gotówką/kartą` in `payments_method` alongside the real values. Free text
 * there is not harmless — every consumer that branches on the method
 * (IMMEDIATE_PAYMENT_METHODS auto-paid, the White List gate, export advisories,
 * the dashboard split) silently treats an unrecognised string as "not that",
 * so the invoice quietly drops out of each rule.
 *
 * Returning null rather than guessing is deliberate: null means "unknown", and
 * the deterministic detectors downstream still get their chance at it — whereas
 * an invented string short-circuits them while looking like an answer.
 */
export function normalizePaymentMethod(
  value: string | null | undefined,
): PaymentMethodValue | null {
  if (!value) return null;
  const cleaned = String(value).trim().toLowerCase();
  return (PAYMENT_METHOD_VALUES as readonly string[]).includes(cleaned)
    ? (cleaned as PaymentMethodValue)
    : null;
}

/** Payment methods indicating invoice is already paid at time of issue */
export const IMMEDIATE_PAYMENT_METHODS: string[] = ['cash', 'card', 'voucher', 'mobile', 'compensation', 'cod'];

// Invoice status
export type InvoiceStatus = 'unpaid' | 'paid' | 'exported';

// Invoice source
export type InvoiceSource = 'email' | 'upload' | 'ksef' | 'import';

// Invoice direction: 'purchase' (cost, default) vs 'sale' (revenue, KSeF Subject1 sync)
export type InvoiceDirection = 'purchase' | 'sale';

// Derived settlement status of a sale-direction invoice (Task A1). NEVER
// stored — always computed from invoice_payments at read time:
//   'unpaid'  — 0 paid
//   'partial' — 0 < paid < gross
//   'paid'    — paid >= gross (overpayment included; outstanding clamps at 0)
// `overdue` is a derived flag (outstanding > 0 AND paymentDate < today), not a
// status of its own — but the list filter accepts it as a pseudo-value.
export type SettlementStatus = 'unpaid' | 'partial' | 'paid';
export type SettlementFilter = SettlementStatus | 'overdue';

// One recorded payment against a sale invoice. `amount` is in the invoice
// currency; `amountPln` uses the invoice's own fxRate (revenue-anchor NBP
// rate — FX differences vs the actual payment-day rate are out of scope).
export interface InvoicePaymentItem {
  id: string;
  invoiceId: string;
  amount: number;
  amountPln: number;
  paidAt: string; // YYYY-MM-DD
  note: string | null;
  createdByName: string;
  createdAt: string; // ISO timestamp
}

export interface AddInvoicePaymentDto {
  amount?: number; // omitted = full outstanding balance
  paidAt: string; // YYYY-MM-DD
  note?: string;
}

export interface AddInvoicePaymentResponse {
  payment: InvoicePaymentItem;
  // Derived settlement AFTER this payment was applied.
  paidAmount: number;
  paidAmountPln: number;
  outstanding: number; // clamped at 0
  settlementStatus: SettlementStatus;
  // True when total paid now exceeds the invoice gross (overpayment allowed
  // but flagged so the UI can warn).
  overpayment: boolean;
}

// Receivables by customer (GET /companies/:nip/receivables): open (outstanding
// > 0) sale invoices grouped per customer, PLN-normalized, sorted by
// outstandingPln desc.
export interface ReceivableCustomer {
  customerName: string;
  customerNip: string | null;
  invoiceCount: number; // open invoices only
  outstandingPln: number;
  overduePln: number; // subset of outstandingPln already past due
  oldestDueDate: string | null; // earliest payment_date among open invoices
}

const warsawDateFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's calendar date in Europe/Warsaw as YYYY-MM-DD (pods run UTC; browsers run anywhere). */
export function warsawTodayYmd(now: Date = new Date()): string {
  return warsawDateFmt.format(now);
}

/** Combining marks dropped after NFKD; ł/Ł have no decomposition, so they are mapped by hand. */
export function stripPolishDiacritics(s: string): string {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l').replace(/Ł/g, 'L');
}

// Customer statement PDF (POST /companies/:nip/sales/statement/pdf).
export const STATEMENT_TYPES = ['statement', 'balance_confirmation'] as const;
export type StatementType = (typeof STATEMENT_TYPES)[number];
export const STATEMENT_LANGS = ['pl', 'en'] as const;
export type StatementLang = (typeof STATEMENT_LANGS)[number];

export interface SalesStatementCustomer {
  key: string; // 'nip:…' | 'ctr:…' | 'name:…' — opaque to the client
  name: string;
  nip: string | null;
  address: string | null; // one line; tells same-named customers apart
  contractorId: string | null;
  invoiceCount: number; // every sale invoice, settled ones included
  currencies: string[];
}

export interface SalesStatementRequest {
  customerKey?: string; // exactly one of customerKey | contractorId
  contractorId?: string;
  type: StatementType;
  lang: StatementLang;
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD, not after today (Europe/Warsaw)
  includePaid: boolean;
}

const STATEMENT_FILE_PREFIX: Record<StatementLang, Record<StatementType, string>> = {
  pl: { statement: 'wyciag', balance_confirmation: 'potwierdzenie-salda' },
  en: { statement: 'statement', balance_confirmation: 'balance-confirmation' },
};

/** One file name for the Content-Disposition header and the browser save. */
export function salesStatementFileName(
  type: StatementType,
  lang: StatementLang,
  customerName: string,
  to: string,
): string {
  const slug = stripPolishDiacritics(customerName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  const fallback = lang === 'pl' ? 'klient' : 'customer';
  return `${STATEMENT_FILE_PREFIX[lang][type]}-${slug || fallback}-${to}.pdf`;
}

// ==================== COMBINED PAYMENTS (płatność łączona) ====================
// One bank/card transaction (date + amount typed in by accounting) covering
// several PURCHASE invoices — e.g. an Allegro cart paid as one charge with
// invoices from different sellers. The matcher finds subsets of already-PAID
// cost invoices (the card charge precedes the invoices, so they arrive paid)
// whose gross amounts sum EXACTLY to the transaction amount; confirming a
// combination creates a CombinedPayment and links the invoices. Payment
// status is never changed by linking or unlinking — this is accounting
// documentation only.
//
// SALES side ("Dopasuj wpłatę"): one customer transfer (often foreign, with
// no invoice numbers in the title) covering several SALE invoices of one
// customer in one currency. Matching runs over the invoices' OUTSTANDING
// balances; small differences (bank fees etc.) up to 1.00 in the currency
// are acceptable. Confirming links the invoices AND registers one
// invoice_payments row per invoice (full outstanding — everything ends
// 'paid'; the difference is knowingly ignored but stays visible on the
// record). Deleting the record cascade-deletes exactly those payment rows,
// restoring the outstanding balances.

export interface CombinedPaymentSearchRequest {
  transactionDate: string; // YYYY-MM-DD (bank statement/booking date)
  amount: number; // PLN
}

// Invoice as shown inside a proposed/confirmed combination — enough for the
// accountant to verify and copy into the accounting system.
export interface CombinedPaymentInvoiceRef {
  id: string;
  invoiceNumber: string | null;
  // Counterparty: the vendor for purchase-side refs, the customer for
  // sale-side refs (kept in one field so both flows share the UI).
  vendorName: string | null;
  issueDate: string | null; // YYYY-MM-DD
  saleDate: string | null; // YYYY-MM-DD
  gross: number | null;
  currency: string | null;
  status: InvoiceStatus;
  // Sales matching only: gross minus registered payments — the amount the
  // matcher actually sums (absent on purchase-side refs).
  outstanding?: number | null;
}

export interface CombinedPaymentCombination {
  invoices: CombinedPaymentInvoiceRef[];
  sum: number; // PLN, sum of invoice gross amounts
  // sum - transaction amount in PLN. 0 = exact; non-zero only in
  // `approximate` results and MUST be surfaced prominently in the UI.
  difference: number;
}

export interface CombinedPaymentSearchResult {
  exact: CombinedPaymentCombination[];
  // Nearest inexact combinations — returned ONLY when `exact` is empty.
  approximate: CombinedPaymentCombination[];
  candidateCount: number; // pool size the search ran over
  truncated: boolean; // true when more combinations may exist
  // Purchase matching only: the ±16-day candidate window actually used.
  // Sales matching has no date window (payment terms vary) — absent there.
  windowFrom?: string; // YYYY-MM-DD
  windowTo?: string; // YYYY-MM-DD
}

export interface CreateCombinedPaymentDto {
  transactionDate: string; // YYYY-MM-DD
  amount: number; // PLN — server re-verifies invoices sum EXACTLY to this
  invoiceIds: string[];
  note?: string;
}

export interface CombinedPaymentDetail {
  id: string;
  companyNip: string;
  transactionDate: string; // YYYY-MM-DD
  amount: number;
  currency: string;
  note: string | null;
  createdByName: string;
  createdAt: string; // ISO timestamp
  invoices: CombinedPaymentInvoiceRef[];
}

// ---- Sales side ("Dopasuj wpłatę") ----

// One (customer, currency) pair with open sale invoices — the selector
// options in the sales match dialog. Foreign customers may have no NIP, so
// identity is customerNip when present, else customerName.
export interface SalesCombinedPaymentCustomer {
  customerNip: string | null;
  customerName: string | null;
  currency: string; // normalized; invoice currency NULL reported as 'PLN'
  openCount: number; // open (outstanding > 0), not-yet-linked invoices
  outstanding: number; // total outstanding in this currency
}

export interface SalesCombinedPaymentSearchRequest {
  transactionDate: string; // YYYY-MM-DD (transfer booking date)
  amount: number; // in `currency`
  currency: string;
  customerNip?: string | null;
  customerName?: string | null; // used when the customer has no NIP
}

export interface CreateSalesCombinedPaymentDto {
  transactionDate: string; // YYYY-MM-DD
  amount: number; // in `currency`; |sum(outstanding) - amount| <= 1.00
  currency: string;
  invoiceIds: string[];
  note?: string;
}

// Accounting note on an invoice (Task A2): append-only, author + timestamp
// recorded. `editedAt`/`editedByName` are present ONLY after an admin edit —
// the original author fields (createdByName/createdByEmail/createdAt) are
// never overwritten.
export interface InvoiceNote {
  id: string;
  invoiceId: string;
  body: string;
  createdByName: string;
  createdByEmail: string;
  createdAt: string; // ISO timestamp
  editedAt: string | null; // ISO timestamp, set only on admin edit
  editedByName: string | null;
}

export interface CreateInvoiceNoteDto {
  body: string;
}

export interface UpdateInvoiceNoteDto {
  body: string;
}

// ==================== PROJECTS (Task B2) ====================
// Per-company projects shared between purchase and sales invoices. A whole
// invoice OR individual line items may be assigned to a project; the
// ATTRIBUTION RULE (enforced in SQL, see PrismaService project queries) is:
// SUM(line nets assigned to the project, converted with the invoice's fxRate)
// — or, when the invoice has NO line-level assignments at all and its own
// projectId is set, the whole invoice's COALESCE(netPln, net). Line
// assignments always win; amounts are never double counted.

export type ProjectStatus = 'active' | 'closed';

export interface Project {
  id: string;
  companyNip: string;
  name: string;
  code: string | null;
  description: string | null;
  color: string | null;
  status: ProjectStatus;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
}

// Row of GET /companies/:nip/projects — project + computed P&L (PLN).
export interface ProjectListItem {
  id: string;
  name: string;
  code: string | null;
  status: ProjectStatus;
  color: string | null;
  costNet: number; // PLN, purchase-side attribution
  revenueNet: number; // PLN, sale-side attribution
  profit: number; // revenueNet - costNet
  marginPct: number | null; // profit / revenueNet * 100; null when revenueNet <= 0
  invoiceCount: number; // distinct invoices attributed (either way)
}

/**
 * Light project row for assignment selectors/filters — carries NO amounts, so
 * it is safe for users who may assign invoices to projects (expense/sales
 * module) but have no 'projects' module grant (no P&L, no analytics).
 */
export interface ProjectOption {
  id: string;
  name: string;
  code: string | null;
  status: ProjectStatus;
  color: string | null;
}

export interface CreateProjectDto {
  name: string;
  code?: string;
  description?: string;
  color?: string;
}

export interface UpdateProjectDto {
  name?: string;
  code?: string | null;
  description?: string | null;
  color?: string | null;
  status?: ProjectStatus; // 'closed' = close the project (history kept)
}

export interface ProjectMonthlyPoint {
  month: string; // YYYY-MM
  costNet: number;
  revenueNet: number;
}

export interface ProjectCounterparty {
  name: string | null;
  nip: string | null;
  total: number; // PLN net attributed to the project
}

// GET /companies/:nip/projects/:id/analytics. All amounts PLN-normalized.
export interface ProjectAnalytics {
  costNet: number;
  revenueNet: number;
  profit: number;
  marginPct: number | null;
  invoiceCounts: { purchase: number; sale: number };
  // Rolling window: the last 12 calendar months ending with the current
  // month, zero-filled, bucketed by COALESCE(dateOfSale, invoiceIssueDate).
  // Totals above are NOT windowed — they cover the project's whole history.
  monthly: ProjectMonthlyPoint[];
  topCostContractors: ProjectCounterparty[];
  topRevenueCustomers: ProjectCounterparty[];
}

// PUT /companies/:nip/invoices/:id/project — null clears the assignment.
export interface AssignInvoiceProjectDto {
  projectId: string | null;
}

// PUT /companies/:nip/invoices/:id/line-items/projects — bulk, replaces the
// assignments of exactly the given lines (lines not listed are untouched).
export interface LineItemProjectAssignment {
  lineItemId: string;
  projectId: string | null;
}

export interface AssignLineItemProjectsDto {
  assignments: LineItemProjectAssignment[];
}

// ==================== PROJECT TAG RULES ====================
// Auto-assignment of invoices to projects by tag. A project's rules are a set
// of tag ids (module 'expense' or 'sales'); the rule engine settles an
// invoice's whole-invoice project assignment on every tag mutation.

// Provenance of invoices.projectId: 'manual' assignments are never touched by
// the rule engine; 'rule' assignments are revoked when the invoice loses its
// last tag matching the project's rules (or the rule is deleted).
// 'correction': auto-inherited from the corrected invoice (correction-follow
// automation, 2026-07-13 spec). Untouchable by the tag-rule engine (which only
// ever modifies 'rule' assignments) and always overridable manually.
export type ProjectAssignedBy = 'manual' | 'rule' | 'correction';

// GET/PUT /companies/:nip/projects/:id/tag-rules — PUT replaces the full set.
export interface ProjectTagRulesDto {
  tagIds: string[];
}

// POST /companies/:nip/projects/:id/tag-rules/apply?dryRun=true|false —
// retroactive application of a project's rules to existing invoices.
// matched   = invoices carrying at least one rule tag;
// assigned  = matched invoices that were (or would be, when dryRun) assigned:
//             no project yet, or already rule-assigned to this project;
// conflicts = matched invoices already assigned to a DIFFERENT project
//             (never overwritten — counted and skipped).
export interface ProjectTagRuleApplyResult {
  matched: number;
  assigned: number;
  conflicts: number;
}

// POST /companies/:nip/invoices/bulk/project — bulk MANUAL whole-invoice
// assignment (projectId null clears). Mixed directions allowed (a project
// spans both); ids not owned by the company are silently skipped.
export interface BulkAssignInvoiceProjectDto {
  invoiceIds: string[];
  projectId: string | null;
}

export interface BulkAssignInvoiceProjectResult {
  updated: number;
}

// MPP filter options
export type MppFilter = 'all' | 'mpp_only' | 'without_mpp';

// Date type for filtering. 'sale_or_issue_date' filters on
// COALESCE(date_of_sale, invoice_issue_date) — the codebase's sales-month
// convention (same fallback rule as getSalesSummary's bucketing); plain
// 'sale_date' hits the raw nullable column and drops NULL-saleDate rows.
export type DateFilterType =
  | 'issue_date'
  | 'sale_date'
  | 'sale_or_issue_date'
  | 'payment_date'
  | 'created_at';

// Export issue codes for mBank PAIN.001.001.09
export type ExportIssueCode =
  | 'already_paid'
  | 'already_exported'
  | 'non_transfer'
  | 'no_contractor_name'
  | 'no_amount'
  | 'no_bank_account'
  | 'invalid_iban'
  | 'mpp_no_nip'
  | 'mpp_no_vat'
  | 'invalid_date'
  | 'swift_needs_bic'
  | 'missing_creditor_city'
  | 'missing_creditor_country'
  | 'sepa_not_eur'
  | 'mpp_intl_conflict'
  | 'non_iban_account'
  | 'not_vat_payer'
  // Country is present but cannot be mapped to an ISO 3166-1 alpha-2 code.
  // PAIN.001 <Ctry> is pattern-restricted to [A-Z]{2}, so a value like
  // "Canada" makes the WHOLE file fail XSD validation, not just this row.
  | 'invalid_country_format'
  // The company has no bank account in the invoice's currency — the export
  // is impossible regardless of how complete the invoice data is. Surfaced
  // as an invoice-level issue so the operator learns it in the list instead
  // of finding a permanently disabled Export button.
  | 'no_debtor_account_for_currency';

// Invoice list item (for table display)
export interface InvoiceListItem {
  id: string;
  fileName: string;
  pdfUrl: string;
  contractorName: string | null;
  contractorNip: string | null;
  invoiceNumber: string | null;
  issueDate: string | null;
  saleDate: string | null;
  paymentDate: string | null;
  status: InvoiceStatus;
  source: InvoiceSource;
  direction: InvoiceDirection;
  tags: string[]; // Tag IDs
  amount: number | null;
  currency: string | null;
  hasSplitPayment: boolean | null;
  createdAt: string;
  exportedAt: string | null; // Date when invoice was exported to bank
  exportIssues: string[]; // Empty = export-ready, non-empty = reasons why not
  customerNip: string | null; // Detected customer NIP (used to match unassigned invoices to a company)
  customerName: string | null; // Buyer name — the counterparty for direction:'sale' rows
  netAmount: number | null;
  taxAmount: number | null;
  ksefNumber: string | null; // KSeF system ID, when synced via KSeF
  // FA(3) RodzajFaktury from the KSeF XML ('VAT' | 'KOR' | 'ZAL' | 'ROZ' |
  // 'UPR' | 'KOR_ZAL' | 'KOR_ROZ'); null for non-KSeF sources. Lets lists
  // mark correction invoices (KOR*) apart from regular sales invoices.
  rodzajFaktury?: string | null;
  // NBP FX conversion applied at ingest time (null when currency is PLN, or
  // when conversion failed and the invoice still carries its face-value amounts).
  fxRate: number | null;
  fxRateDate: string | null; // YYYY-MM-DD, the NBP publication date the rate was taken from
  netPln: number | null;
  taxPln: number | null;
  grossPln: number | null;
  // Derived settlement (Task A1) — present ONLY on sale-direction rows.
  // Computed from invoice_payments at read time; amounts in invoice currency
  // except paidAmountPln. `outstanding` clamps at 0 on overpayment.
  paidAmount?: number;
  paidAmountPln?: number;
  outstanding?: number;
  settlementStatus?: SettlementStatus;
  isOverdue?: boolean;
  daysOverdue?: number; // 0 when not overdue
  // Accounting notes (Task A2) — subquery-derived count, present on every row
  // returned by the invoice list (both directions). `hasNotes` is a plain JS
  // derivation (notesCount > 0), not a stored column.
  notesCount?: number;
  hasNotes?: boolean;
  // Projects (Task B2) — whole-invoice assignment (null when unassigned) and
  // whether any of the invoice's line items carries its own project
  // assignment (line assignments win over projectId for attribution).
  projectId?: string | null;
  projectName?: string | null;
  hasLineProjects?: boolean;
  // Provenance of projectId ('manual' | 'rule'); null/undefined = unassigned
  // or legacy row (treated as manual).
  projectAssignedBy?: ProjectAssignedBy | null;
  // Combined payment (płatność łączona) — set when this purchase invoice was
  // paid as part of one multi-invoice bank/card transaction. The list badge
  // keys off this; full payment data comes from the detail endpoint.
  combinedPaymentId?: string | null;
  // Employee reimbursement (zwrot do pracownika) — the active case's status,
  // null when the invoice has none. Drives the list chip.
  reimbursementStatus?: ReimbursementStatus | null;
}

// Invoice filter params
export interface InvoiceFilterParams {
  companyNip: string;
  search?: string;
  mppFilter?: MppFilter;
  source?: InvoiceSource | 'all';
  dateType?: DateFilterType;
  dateFrom?: string;
  dateTo?: string;
  tagIds?: string[];
  status?: InvoiceStatus | 'all';
  direction?: InvoiceDirection;
  // Derived settlement filter (Task A1) — only meaningful for direction:
  // 'sale'; ignored for purchase lists (purchases keep the legacy `status`).
  settlement?: SettlementFilter;
  // Accounting notes filter (Task A2): true = only invoices WITH at least one
  // note, false = only invoices with NO notes ("bez opisu"). Undefined =
  // unfiltered. Applies to both directions.
  hasNotes?: boolean;
  // Projects (Task B2): only invoices attributed to this project — via line
  // assignments, or via a whole-invoice assignment when no line of the
  // invoice is assigned to any project (mirrors the attribution rule).
  projectId?: string;
  // Employee-reimbursement filter (zwroty) — purchase lists only.
  reimbursementFilter?: ReimbursementFilter;
  // Tag-level visibility (RBAC, Task B2): when set, only invoices carrying at
  // least one of these tags are returned. Undefined = unrestricted.
  visibleTagIds?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Invoice detail (full data)
export interface InvoiceDetail extends InvoiceListItem {
  vendorAddress: string | null;
  vendorStreet: string | null;
  vendorPostalCode: string | null;
  vendorCity: string | null;
  vendorCountry: string | null;
  customerName: string | null;
  customerNip: string | null;
  customerAddress: string | null;
  netAmount: number | null;
  taxAmount: number | null;
  paymentMethod: PaymentMethodValue | string | null;
  bankAccount: string | null;
  /** Beneficiary bank's SWIFT/BIC as printed on THIS document (OCR/AI). Takes
   *  precedence over the contractor card's BIC in the mBank export, which is
   *  only the reusable master. Optional: older payloads predate the column. */
  swiftBic?: string | null;
  description: string | null;
  email: string | null;
  vatPayerData: VatPayerData | null;
  ksefNumber: string | null;
  ksefVerificationUrl: string | null;
  lineItems: KsefLineItem[] | null;
  ksefMetadata: KsefMetadata | null;
  // Correction linkage (2026-07-13 spec) — one entry per DaneFaKorygowanej
  // ref, in XML order. `id` is the locally-resolved original (null when the
  // referenced invoice is not in our DB). Only present on KOR* invoices.
  correctedInvoices?: Array<{
    id: string | null;
    invoiceNumber: string | null;
    ksefNumber: string | null;
  }> | null;
  contractorId?: string | null;
  isByCar?: boolean;
  tripDestination?: string | null;
  // Payment history (Task A1) — array for sale-direction invoices (possibly
  // empty), null for purchases (payments never apply to them).
  payments?: InvoicePaymentItem[] | null;
  // Combined payment (płatność łączona) — full record incl. the sibling
  // invoices covered by the same transaction; null when not linked.
  combinedPayment?: CombinedPaymentDetail | null;
}

export interface KsefLineItem {
  id: string;
  ordinal: number;
  lineNumber: string | null;
  description: string;
  unitPrice: number | null;
  quantity: number | null;
  unit: string | null;
  vatRate: string | null;
  netValue: number | null;
  vatValue: number | null;
  // Per-line project assignment (Task B2) — wins over the invoice's projectId.
  projectId?: string | null;
  deliveryDate?: string | null;
  gtuin?: string | null;
  cn?: string | null;
  pkob?: string | null;
  gtu?: string | null;
}

export interface KsefMetadata {
  formType: string;
  xmlVersion?: string;
  verificationHash?: string;
  rodzajFaktury?: string;
  // Correction linkage (RodzajFaktury KOR/KOR_ZAL/KOR_ROZ) — 2026-07-13 spec.
  // Fa/PrzyczynaKorekty: issuer's stated reason for the correction.
  przyczynaKorekty?: string;
  // Fa/TypKorekty (optional in FA schema): correction accounting-period type.
  typKorekty?: number;
  // Fa/DaneFaKorygowanej (repeatable): references to the corrected invoice(s).
  daneFaKorygowanej?: Array<{
    dataWystFaKorygowanej?: string;
    nrFaKorygowanej?: string;
    nrKsefFaKorygowanej?: string;
  }>;
  wariantFormularza?: number;
  dataWytworzeniaFa?: string;
  systemInfo?: string;
  miejsceWystawienia?: string;
  okresOd?: string;
  okresDo?: string;
  podmiot1Kontakt?: { email?: string; telefon?: string };
  prefiksPodatnika?: string;
  podmiot2?: {
    nrKlienta?: string;
    /** Buyer id from the DaneIdentyfikacyjne CHOICE when it is not a Polish
     *  NIP: EU VAT id (kodUE + nrVatUE) or other-foreign id (kodKraju + nrId).
     *  Only the EU form also lands in invoices.customer_nip. */
    kodUE?: string;
    nrVatUE?: string;
    kodKraju?: string;
    nrId?: string;
    adres?: {
      kodKraju?: string;
      adresL1?: string;
      adresL2?: string;
      ulica?: string;
      nrDomu?: string;
      nrLokalu?: string;
      kodPocztowy?: string;
      miejscowosc?: string;
    };
    kontakt?: { email?: string; telefon?: string };
  };
  podmiot3?: {
    nip?: string;
    nazwa?: string;
    adres?: {
      kodKraju?: string;
      adresL1?: string;
      adresL2?: string;
      ulica?: string;
      nrDomu?: string;
      nrLokalu?: string;
      kodPocztowy?: string;
      miejscowosc?: string;
    };
    kontakt?: { email?: string; telefon?: string };
  };
  stawkiVat?: { stawka: string; netto: number; vat: number }[];
  rachunkiBankowe?: { nrRB: string; nazwaBanku?: string; swift?: string }[];
  rachunekBankowyMP?: { nrRB: string };
  zaplacono?: number;
  /** Platnosc/DataZaplaty — the date the invoice was actually paid (accompanies zaplacono=1). */
  dataZaplaty?: string;
  platnoscInna?: string;
  opisPlatnosci?: string;
  adnotacje?: Record<string, unknown>;
  dodatkowyOpis?: { klucz: string; wartosc: string }[];
  zamowienie?: {
    wartoscZamowienia?: number;
    wiersze?: {
      nrZamowieniaObcy?: string;
      nrZamowienia?: string;
      dataZamowienia?: string;
    }[];
  };
  wz?: { nrWZ: string; dataWZ?: string }[];
  /**
   * FA(3) `Fa/Rozliczenie` — additional charges and deductions applied on top of
   * the VAT total.
   *
   * The previous shape (an array of advance-invoice settlements keyed by
   * `nrFaZaliczkowej`/`dataWplaty`) described elements that do NOT exist in the
   * FA(3) schema, so it was never populated for a single one of the 646 stored
   * invoices. `doZaplaty` is the one that matters: it is the amount actually
   * owed — P_15 plus charges, minus deductions — and differs from the invoice
   * total whenever an issuer applies either.
   */
  rozliczenie?: {
    obciazenia?: { kwota: number; powod: string }[];
    sumaObciazen?: number;
    odliczenia?: { kwota: number; powod: string }[];
    sumaOdliczen?: number;
    /** P_15 + charges − deductions. */
    doZaplaty?: number;
    /** Overpayment to be refunded/settled, mutually exclusive with doZaplaty. */
    doRozliczenia?: number;
  };
  stopka?: string;
}

// Government registry lookups (Polish MF White List + EU VIES)
export type RegistrySource = 'wl' | 'vies';

export interface RegistryCompanyData {
  /** Normalized lookup id: bare 10-digit NIP ('9372437672') or EU VAT id ('DE811115368'). */
  id: string;
  source: RegistrySource;
  /** wl: subject exists on the White List; vies: the VAT number is valid. */
  found: boolean;
  name: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  /** 'PL' for White List answers, the ISO-2 country prefix for VIES. */
  country: string | null;
  /** Unparsed single-line address as returned by the registry. */
  rawAddress: string | null;
  /** wl: 'Czynny' | 'Zwolniony' | 'Niezarejestrowany'; vies: 'VIES-valid' | 'VIES-invalid'. */
  vatStatus: string | null;
  regon: string | null;
  krs: string | null;
  /** White List NRB accounts (26 digits); [] for VIES. */
  accountNumbers: string[];
  hasVirtualAccounts: boolean | null;
  /** ISO timestamp of the registry answer this data comes from. */
  checkedAt: string;
}

export interface ContractorRegistryRefreshResult {
  contractor: ContractorDetail;
  registry: RegistryCompanyData;
  /** White List account comparison; null for VIES lookups. */
  accountComparison: {
    /** Stored contractor accounts present on the White List. */
    matched: string[];
    /** White List accounts not stored on the contractor. */
    missingLocally: string[];
    /** Stored contractor accounts absent from the White List answer. */
    notOnWhitelist: string[];
  } | null;
}

// VAT White List verification data
export interface VatPayerData {
  accountAssigned: boolean | null; // true = account is on VAT white list, false = not on list, null = not checked
  requestDateTime: string | null; // when the verification was performed
  requestId: string | null; // unique ID from the VAT API for this verification
}

// Invoice summary (legacy, keep for backwards compatibility)
export interface InvoiceSummary {
  id: string;
  fileName: string;
  vendorName: string | null;
  vendorNip: string | null;
  customerNip: string | null;
  gross: number | null;
  currency: string | null;
  invoiceDate: string | null;
  createdAt: string;
  isInvoice: boolean;
}

// Bulk actions
export interface BulkUpdateTagsDto {
  invoiceIds: string[];
  addTagIds?: string[];
  removeTagIds?: string[];
}

export interface BulkUpdateStatusDto {
  invoiceIds: string[];
  status: InvoiceStatus;
}

// Assign unassigned invoices to a company
export interface AssignInvoicesRequest {
  invoiceIds: string[];
  targetCompanyNip: string;
}

export interface AssignInvoicesResponse {
  assigned: number;
}

// Bulk delete invoices (admin-only)
export interface BulkDeleteInvoicesResponse {
  deleted: number;
}

// Update invoice DTO
export interface UpdateInvoiceDto {
  vendorName?: string | null;
  vendorNip?: string | null;
  vendorAddress?: string | null;
  vendorStreet?: string | null;
  vendorPostalCode?: string | null;
  vendorCity?: string | null;
  vendorCountry?: string | null;
  customerName?: string | null;
  customerNip?: string | null;
  invoiceNumber?: string | null;
  invoiceIssueDate?: string | null;
  dateOfSale?: string | null;
  paymentDate?: string | null;
  gross?: number | null;
  net?: number | null;
  tax?: number | null;
  currency?: string | null;
  hasSplitPayment?: boolean | null;
  bankAccount?: string | null;
  /** Beneficiary bank's SWIFT/BIC for THIS invoice. Editable because a foreign
   *  vendor prints it on the document and the contractor card may not have it
   *  yet (or at all, for a one-off non-IBAN payee). */
  swiftBic?: string | null;
  paymentMethod?: PaymentMethodValue | string | null;
  status?: InvoiceStatus;
  contractorId?: string | null;
  isByCar?: boolean;
  tripDestination?: string | null;
}

// Dashboard types
export interface DashboardSummary {
  totalGross: number;
  totalTax: number;
  invoiceCount: number;
  averageGross: number;
  totalUnpaidGross: number;
  unpaidCount: number;
  currency: string;
}

export interface DashboardMonthlyCost {
  month: string; // "2025-01"
  totalGross: number;
  totalTax: number;
  invoiceCount: number;
}

export interface DashboardMonthlyTagCost {
  month: string;       // "2026-01"
  tagId: string | null;
  tagName: string;
  tagColor: string;
  totalNet: number;
  invoiceCount: number;
}

export interface DashboardMonthlyPaymentMethodCost {
  month: string;       // "2026-01"
  group: string;
  label: string;
  totalGross: number;
  invoiceCount: number;
}

export interface DashboardUnpaidMonth {
  month: string;
  unpaidGross: number;
  unpaidCount: number;
}

export interface DashboardResponse {
  year: number;
  availableYears: number[];
  summary: DashboardSummary;
  monthlyCosts: DashboardMonthlyCost[];
  monthlyTagCosts: DashboardMonthlyTagCost[];
  monthlyPaymentMethodCosts: DashboardMonthlyPaymentMethodCost[];
  unpaidByMonth: DashboardUnpaidMonth[];
}

// Sales summary (KSeF Subject1 revenue, direction === 'sale')
export interface SalesSummaryMonth {
  month: number; // 1-12
  net: number;
  gross: number;
  count: number;
}

export interface SalesSummary {
  year: number;
  totalNet: number;
  totalGross: number;
  invoiceCount: number;
  avgNet: number;
  currentMonthNet: number; // current calendar month's net; 0 when `year` isn't the current year
  months: SalesSummaryMonth[]; // always 12 entries (Jan-Dec), zero-filled
}

// Financial overview (CFO dashboard, Task E1): revenue vs cost, cash flow, aging, VAT position.
export interface FinancialMonth {
  month: number;            // 1-12
  revenueNet: number;       // sales, net, bucketed by COALESCE(date_of_sale, invoice_issue_date)
  costNet: number;          // purchases, net, same bucketing
  marginNet: number;        // revenueNet - costNet
  marginPct: number | null; // marginNet/revenueNet*100, null when revenueNet = 0
  cashInNet: number;        // paid sales invoices (status='paid'), gross, bucketed by payment_date
  cashOutNet: number;       // paid purchase invoices, gross, bucketed by payment_date
  vatDue: number;           // sales tax
  vatInput: number;         // purchase tax
}

export interface AgingBuckets {
  current: number;
  d30: number;
  d60: number;
  d90: number;
  older: number;
}

export interface CounterpartyTotal {
  name: string;
  nip: string | null;
  total: number;
  count: number;
}

export interface FinancialOverview {
  year: number;
  availableYears: number[];
  kpis: {
    revenueYtd: number;
    costYtd: number;
    marginYtd: number;
    marginPct: number | null;
    vatPosition: number;              // sum(vatDue) - sum(vatInput)
    revenueMoM: number | null;        // % change latest month with data vs previous
    receivablesTotal: number;
    payablesTotal: number;
  };
  months: FinancialMonth[];           // 12, zero-filled
  receivables: AgingBuckets;          // unpaid SALES by days past payment_date (relative to today)
  payables: AgingBuckets;             // unpaid PURCHASES, same
  topCustomers: CounterpartyTotal[];  // top 5 by revenue (sales: customer_name/customer_nip)
  topVendors: CounterpartyTotal[];    // top 5 by cost (purchases: vendor_name/vendor_nip)
  /**
   * Payroll cost block — what people actually cost, next to invoice
   * revenue/cost. `null` when the caller may see the financial overview
   * ('dashboard' module) but NOT salaries (needs 'employees' module access or
   * the 'hr'/'admin' role): the field is then omitted entirely, never zeroed.
   */
  payroll: PayrollOverview | null;
}

/**
 * One settlement month of aggregated payroll. All figures in PLN, gross-up
 * from the statutory engine (modules/hr/payroll/payroll-engine.ts).
 *
 * b2b is EXCLUDED from every field except `b2bCost`: a contractor's cost
 * already arrives as a purchase invoice and is inside FinancialOverview's
 * `costYtd` — adding it to `employerCost` would double-count it.
 */
export interface PayrollMonth {
  month: number;         // 1–12
  gross: number;         // brutto (non-b2b)
  employeeZus: number;   // składki ZUS pracownika (emerytalna + rentowa + chorobowa)
  health: number;        // składka zdrowotna
  pit: number;           // zaliczka PIT
  net: number;           // netto "na rękę"
  employerZus: number;   // składki pracodawcy (emerytalna, rentowa, wypadkowa, FP/FS, FGŚP, PPK)
  employerCost: number;  // całkowity koszt pracodawcy = gross + employerZus
  b2bCost: number;       // contractors' invoice net — ALREADY counted in costYtd
  headcount: number;     // employees contributing to this month (b2b excluded)
}

/** Per-employment-type totals. b2b is excluded (see PayrollMonth.b2bCost). */
export interface PayrollTypeTotal {
  employmentType: EmploymentType;
  headcount: number;     // distinct employees who contributed in the period
  gross: number;
  employerCost: number;
}

export interface PayrollOverview {
  year: number;
  /** Elapsed months only for the current year (no projection); all 12 for past years. */
  months: PayrollMonth[];
  kpis: {
    employerCostYtd: number;
    grossYtd: number;
    netYtd: number;
    employeeZusYtd: number;
    healthYtd: number;
    pitYtd: number;
    employerZusYtd: number;
    /** employeeZus + health + pit + employerZus — the total "danina". */
    taxWedgeYtd: number;
    /** taxWedgeYtd / employerCostYtd; null when employerCostYtd is 0. */
    taxWedgePct: number | null;
    /** Contractors (b2b) — flagged: already inside FinancialOverview.kpis.costYtd. */
    b2bCostYtd: number;
    /** Active today (b2b excluded). */
    headcount: number;
    /** employerCostYtd / headcount; null when headcount is 0. */
    avgEmployerCostPerEmployee: number | null;
  };
  byType: PayrollTypeTotal[];
  /**
   * Employees skipped in at least one month because they have no employment
   * type or no active rate. Their cost is MISSING from the totals — the UI must
   * warn ("N pracowników bez stawki/typu — koszty niepełne") rather than
   * present a silently too-low number.
   */
  incompleteEmployees: number;
}

// Contractor Dashboard types
export interface ContractorDashboardSummary {
  totalTurnoverNet: number;
  totalTurnoverGross: number;
  invoiceCount: number;
  averageInvoiceNet: number;
  lastInvoiceDate: string | null;
  currency: string;
}

export interface ContractorMonthlyTurnover {
  month: string;
  totalNet: number;
  totalGross: number;
  invoiceCount: number;
}

export interface ContractorMonthlyShare {
  month: string;
  contractorNet: number;
  companyTotalNet: number;
  sharePercent: number;
}

export interface ContractorDashboardResponse {
  year: number;
  availableYears: number[];
  contractor: ContractorListItem;
  // Purchase side (this counterparty as OUR SUPPLIER) — historical names.
  summary: ContractorDashboardSummary;
  monthlyTurnover: ContractorMonthlyTurnover[];
  monthlyShare: ContractorMonthlyShare[];
  // Sales side (this counterparty as OUR CUSTOMER), same year window.
  salesSummary: ContractorDashboardSummary;
  monthlySales: ContractorMonthlyTurnover[];
  monthlySalesShare: ContractorMonthlyShare[];
  // salesSummary.totalTurnoverNet - summary.totalTurnoverNet for the year.
  balanceNet: number;
}

// KSeF types
export interface KsefConfig {
  enabled: boolean;
  environment: KsefEnvironment;
  hasCertificate: boolean;
  certificateExpiry: string | null;
  certificateFingerprint: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  syncFromDate: string | null;
}

export interface UpdateKsefConfigDto {
  enabled?: boolean;
  environment?: KsefEnvironment;
  syncFromDate?: string;
}

export interface KsefSyncResult {
  success: boolean;
  invoicesFound: number;
  invoicesImported: number;
  invoicesSkipped: number;
  errors: string[];
  /** Invoices imported from the Subject2 (buyer) loop this run. */
  purchases: number;
  /** Invoices imported from the Subject1 (seller) loop this run. */
  sales: number;
}

export interface KsefTestConnectionResult {
  success: boolean;
  certificateValid: boolean;
  apiReachable: boolean;
  authenticationWorks: boolean;
  errors: string[];
}

// ==================== MILEAGE / VEHICLE TYPES ====================

export interface VehicleListItem {
  id: string;
  displayName: string;
  registrationNumber: string;
  initialOdometer: number;
  initialOdometerDate: string;
  isActive: boolean;
  notes: string | null;
  drivers: VehicleDriverItem[];
}

export interface CreateVehicleDto {
  displayName: string;
  registrationNumber: string;
  initialOdometer: number;
  initialOdometerDate: string; // YYYY-MM-DD
  notes?: string;
}

export interface UpdateVehicleDto {
  displayName?: string;
  registrationNumber?: string;
  initialOdometer?: number;
  initialOdometerDate?: string;
  isActive?: boolean;
  notes?: string | null;
}

export interface VehicleDriverItem {
  id: string;
  fullName: string;
  isEmployee: boolean;
  isDefault: boolean;
}

export interface CreateDriverDto {
  fullName: string;
  isEmployee?: boolean;
  isDefault?: boolean;
}

export interface UpdateDriverDto {
  fullName?: string;
  isEmployee?: boolean;
  isDefault?: boolean;
}

export interface StructuredAddress {
  country?: string;    // default "Polska"
  city?: string;
  street?: string;
  number?: string;
  clientName?: string; // e.g. "Nitrex Metal Sp. z o.o."
}

export interface StandardRouteListItem {
  id: string;
  vehicleId: string | null;
  routeFrom: string;
  routeTo: string;
  distanceKm: number;
  defaultPurpose: string | null;
  isRoundTrip: boolean;
  googleMapsData: unknown | null;
  routeFromAddress?: StructuredAddress;
  routeToAddress?: StructuredAddress;
}

export interface CreateStandardRouteDto {
  vehicleId?: string;
  routeFrom: string;
  routeTo: string;
  distanceKm: number;
  defaultPurpose?: string;
  isRoundTrip?: boolean;
  routeFromAddress?: StructuredAddress;
  routeToAddress?: StructuredAddress;
}

export interface UpdateStandardRouteDto {
  vehicleId?: string | null;
  routeFrom?: string;
  routeTo?: string;
  distanceKm?: number;
  defaultPurpose?: string | null;
  isRoundTrip?: boolean;
  routeFromAddress?: StructuredAddress;
  routeToAddress?: StructuredAddress;
}

export interface CalculateDistanceDto {
  origin: string;
  destination: string;
  waypoints?: string[];
  computeAlternatives?: boolean;
}

export interface CalculateDistanceResult {
  distanceKm: number;
  durationMinutes: number;
  polyline?: string;
  description?: string;
}

export interface PlaceSearchResult {
  name: string;
  formattedAddress: string;
  address: StructuredAddress;
}

export type MileageRecordStatus = 'draft' | 'reconciled' | 'finalized';

export interface MileageRecordSummary {
  id: string;
  vehicleId: string;
  year: number;
  month: number;
  odometerStart: number | null;
  odometerEnd: number | null;
  totalKm: number | null;
  status: MileageRecordStatus;
  entryCount: number;
  entriesKm: number;
}

export interface MileageRecordDetail extends MileageRecordSummary {
  entries: MileageEntryItem[];
  reconciliationData: unknown | null;
  pdfS3Key: string | null;
}

export type MileageEntrySource = 'manual' | 'invoice' | 'standard_route' | 'ai_suggested';

export interface MileageEntryItem {
  id: string;
  entryNumber: number;
  tripDate: string;
  routeDescription: string;
  purpose: string;
  distanceKm: number;
  driverName: string;
  isEmployee: boolean;
  odometerHandover: number | null;
  odometerReturn: number | null;
  isRoundTrip: boolean;
  invoiceId: string | null;
  standardRouteId: string | null;
  source: MileageEntrySource;
}

export interface CreateMileageEntryDto {
  tripDate: string;
  routeDescription: string;
  purpose: string;
  distanceKm: number;
  driverName: string;
  isEmployee?: boolean;
  odometerHandover?: number;
  odometerReturn?: number;
  isRoundTrip?: boolean;
  invoiceId?: string;
  standardRouteId?: string;
  source?: MileageEntrySource;
}

export interface ReorderEntriesDto {
  entryIds: string[];
}

export interface UpdateMileageEntryDto {
  tripDate?: string;
  routeDescription?: string;
  purpose?: string;
  distanceKm?: number;
  driverName?: string;
  isEmployee?: boolean;
  odometerHandover?: number | null;
  odometerReturn?: number | null;
  isRoundTrip?: boolean;
}

export interface TripCandidate {
  invoiceId: string;
  invoiceNumber: string | null;
  contractorName: string | null;
  contractorNip: string | null;
  invoiceDate: string | null;
  amount: number | null;
  reason: 'by_car_contractor' | 'cash_payment' | 'card_payment';
  suggestedRoute: string | null;
  suggestedDistance: number | null;
  suggestedPurpose: string | null;
  alreadyLinked: boolean;
}

export interface ReconciliationResult {
  status: 'balanced' | 'under_logged' | 'over_logged' | 'missing_odometer';
  targetKm: number | null;
  currentKm: number | null;
  deltaKm: number | null;
  suggestions: ReconciliationSuggestion[];
}

export interface ReconciliationSuggestion {
  type: 'add_trip' | 'reduce_distance' | 'remove_trip' | 'ai_suggested';
  description: string;
  routeDescription?: string;
  purpose?: string;
  distanceKm: number;
  driverName?: string;
  entryId?: string;
  invoiceId?: string;
  source?: string;
  confidence: number; // 0-1
}

// ==================== MILEAGE PLACES ====================

export interface RouteAlternative {
  distanceKm: number;
  durationMinutes: number;
  description: string;
  isDefault: boolean;
}

export interface MileagePlace {
  id: string;
  name: string;
  addressCity: string;
  addressStreet?: string;
  addressNumber?: string;
  addressCountry: string;
  distanceKm: number;
  defaultPurpose: string;
  visitWeight: number;
  monthlyMinVisits: number;
  monthlyMaxVisits: number;
  isRoundTrip: boolean;
  routeAlternatives?: RouteAlternative[];
  isActive: boolean;
}

export interface CreateMileagePlaceDto {
  name: string;
  addressCity: string;
  addressStreet?: string;
  addressNumber?: string;
  addressCountry?: string;
  distanceKm: number;
  defaultPurpose: string;
  visitWeight?: number;
  monthlyMinVisits?: number;
  monthlyMaxVisits?: number;
  isRoundTrip?: boolean;
}

export interface UpdateMileagePlaceDto {
  name?: string;
  addressCity?: string;
  addressStreet?: string;
  addressNumber?: string;
  addressCountry?: string;
  distanceKm?: number;
  defaultPurpose?: string;
  visitWeight?: number;
  monthlyMinVisits?: number;
  monthlyMaxVisits?: number;
  isRoundTrip?: boolean;
  isActive?: boolean;
}

export interface GenerateTripsResult {
  generatedCount: number;
  totalKm: number;
  entries: MileageEntryItem[];
}

// ==================== MBANK EXPORT TYPES ====================

export interface MBankPaymentRecord {
  id: string;
  /** Set for employee-reimbursement records: the transfer pays the employee
   *  back (beneficiary = employee, title "Zwrot za ..."); the referenced
   *  invoice's status is NOT flipped to exported — the reimbursement row is. */
  reimbursementId?: string | null;
  isMerged: boolean;
  mergedInvoiceIds: string[];
  contractorName: string | null;
  contractorNip: string | null;
  contractorCity: string | null;
  contractorCountry: string | null;
  /** Creditor street + building number and postal code. Emitted as
   *  StrtNm/BldgNb/PstCd on cross-border transfers only (FATF R.16 / EU
   *  2015/847 want a full beneficiary address); domestic blocks ignore them. */
  contractorStreet?: string | null;
  contractorPostalCode?: string | null;
  invoiceNumber: string;
  bankAccount: string | null;
  paymentDate: string | null;
  amount: number;
  currency: string | null;
  hasSplitPayment: boolean;
  vatAmount?: number | null;
  swiftBic?: string | null;
  chargeBearerCode?: ChargeBearerCode | null;
  vatAccountAssigned?: boolean | null;
}

export interface MBankExportRequest {
  debtorAccount?: CompanyBankAccount;
  debtorAccounts?: CompanyBankAccount[];
  executionDate: string | null;
  payments: MBankPaymentRecord[];
  /** Operator explicitly confirmed re-exporting invoices already marked as
   *  paid (accepting the double-payment risk). Without it such invoices are
   *  rejected server-side. */
  allowAlreadyPaid?: boolean;
}

// ===== Employees =====

export const EMPLOYMENT_TYPES = [
  'mandate_contract',
  'mandate_contract_student',
  'employment_contract',
  'contractor',
  // Board member appointed by resolution (powołanie, art. 201 § 4 KSH):
  // no social insurance title (art. 6 ust. 1 ustawy o sus), obligatory 9%
  // health when remunerated (art. 66 ust. 1 pkt 35a), PIT art. 13 pkt 7.
  // For HR leave rules powolanie behaves like the non-UOP forms (no
  // statutory leave — only unavailability_b2b / remote_work).
  'powolanie',
  // Umowa o dzieło (specific-task contract, art. 627 KC): NOT an insurance
  // title (absent from art. 6 ust. 1 ustawy o sus) → no ZUS and no health;
  // PIT art. 13 pkt 8 with 20%/50% KUP. Must be reported to ZUS on form RUD
  // within 7 days of conclusion (compliance track 'rud_report'). For HR
  // leave rules dzieło is NOT an employment relationship → same treatment
  // as B2B/zlecenie/powołanie (only unavailability_b2b / remote_work).
  'dzielo',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const RATE_TYPES = ['flat_monthly', 'hourly'] as const;
export type RateType = (typeof RATE_TYPES)[number];

export const EMPLOYEE_STATUSES = [
  'active',
  'expiring_soon',
  'expired',
  'terminated',
] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

export type IdDocumentType = 'dowod' | 'passport';

export interface EmployeeListItem {
  id: string;
  companyNip: string;
  firstName: string;
  lastName: string;
  /** null = auto-provisioned from Entra, HR has not set the contract type yet. */
  employmentType: EmploymentType | null;
  status: EmployeeStatus;
  contractExpiryDate: string | null;
  currentRate: { rateType: RateType; amount: number } | null;
  contractor: { id: string; vendorNip: string | null; vendorName: string | null } | null;
  // Entra directory link (HR foundation)
  workEmail: string | null;
  jobTitle: string | null;
  department: string | null;
  managerEmployeeId: string | null;
  managerName: string | null;
  /** True when at least one app user account is linked to this employee.
   *  Purely informational — an Employee record never grants app access. */
  hasAppAccount: boolean;
  archivedAt: string | null;
  updatedAt: string;
  /**
   * Estimated CURRENT-month payroll figures computed from the current rate +
   * employment type (no year-to-date context — "as for January"). null when
   * the rate or the contract type is missing (see payrollNote).
   */
  payroll: EmployeeListPayroll | null;
  payrollNote: EmployeeListPayrollNote | null;
}

/** Monthly payroll summary shown in the employee list (PLN). */
export interface EmployeeListPayroll {
  gross: number;
  net: number;
  employerCost: number;
}

export type EmployeeListPayrollNote = 'no_rate' | 'no_type';

/**
 * Set on the create/update response when a submitted dateOfBirth disagreed with
 * the one derived from a valid PESEL. The PESEL always wins (single source of
 * truth) — this reports the override instead of applying it silently.
 */
export interface PeselOverrideWarning {
  code: 'date_of_birth_overridden';
  /** What the caller sent. */
  submittedDateOfBirth: string;
  /** What was stored (derived from the PESEL). */
  derivedDateOfBirth: string;
}

export interface EmployeeDetail extends EmployeeListItem {
  pesel: string | null;
  dateOfBirth: string | null;
  /** "Moje dane" reveal brute-force lock — hr/admin clears it (POST hr/my-data/:employeeId/unlock). */
  myDataLocked?: boolean;
  /** Derived from the PESEL when one is set; manually set otherwise. */
  sex: Sex | null;
  /** Present only on a create/update response that overrode a conflicting dateOfBirth. */
  peselWarning?: PeselOverrideWarning | null;
  idDocumentType: IdDocumentType | null;
  idDocumentNumber: string | null;
  taxOffice: string | null;
  nfzBranch: string | null;
  privateEmail: string | null;
  privatePhone: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  privateIban: string | null;
  contractStartDate: string | null;
  terminatedAt: string | null;
  hasStudentCard: boolean;
  studentCardExpiry: string | null;
  notes: string | null;
}

export interface EmployeeRate {
  id: string;
  employeeId: string;
  rateType: RateType;
  amount: number;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
}

export interface EmployeeMonthlyHoursRow {
  id: string;
  employeeId: string;
  year: number;
  month: number;
  hours: number;
}

export interface EmployeeDocument {
  id: string;
  employeeId: string;
  label: string;
  fileName: string;
  expiryDate: string | null;
  uploadedAt: string;
  /** Absent/'kartoteka' = uploaded file (deletable, employees download endpoint);
   *  'publication_signed' = read-only signed publication scan surfaced from
   *  document_acceptances (id = acceptance id; download via the publications
   *  returned-download endpoint). */
  source?: 'kartoteka' | 'publication_signed';
  publicationId?: string;
  /** Only for source 'publication_signed': returned = awaiting HR confirmation. */
  acceptanceStatus?: 'returned' | 'signed';
  /** Only for source 'publication_signed': digital-signature check of the scan. */
  signatureVerification?: SignatureVerification | null;
}

export interface CreateEmployeeDto {
  firstName: string;
  lastName: string;
  employmentType: EmploymentType;
  pesel?: string;
  /** Ignored when a valid `pesel` is sent — the PESEL wins (see PeselOverrideWarning). */
  dateOfBirth?: string;
  /** Ignored when a valid `pesel` is sent — the PESEL wins. */
  sex?: Sex;
  idDocumentType?: IdDocumentType;
  idDocumentNumber?: string;
  taxOffice?: string;
  nfzBranch?: string;
  privateEmail?: string;
  privatePhone?: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  privateIban?: string;
  contractStartDate?: string;
  contractExpiryDate?: string;
  notes?: string;
  contractorId?: string;     // type='contractor': existing contractor
  contractorNip?: string;    // type='contractor': will look up or auto-create from BIR
}

export type UpdateEmployeeDto = Partial<CreateEmployeeDto>;

export interface CreateRateDto {
  rateType: RateType;
  amount: number;
  validFrom: string;
}
export type UpdateRateDto = Partial<CreateRateDto> & { validTo?: string | null };

export interface UpsertHoursDto {
  year: number;
  month: number;
  hours: number;
}

export interface CostReportRow {
  employeeId: string;
  fullName: string;
  /** null = auto-provisioned employee whose contract type HR has not set yet. */
  employmentType: EmploymentType | null;
  rateType: RateType | null;
  rateAmount: number | null;
  hours: number | null;
  monthlyCost: number;
  note?: 'no_active_rate' | 'missing_hours';
}

export interface CostReport {
  year: number;
  month: number;
  currency: 'PLN';
  entries: CostReportRow[];
  totals: {
    byType: Partial<Record<EmploymentType, number>>;
    grandTotal: number;
  };
}

// ===== Payroll (Polish gross↔net + employer cost) =====
// Engine reference: docs/superpowers/research/pl-payroll-2026.md

/**
 * Settlement form of the engagement:
 *  - uop              — umowa o pracę (§2 of the reference)
 *  - zlecenie         — umowa zlecenie, standard zleceniobiorca (§3.1)
 *  - zlecenie_student — student/uczeń <26 on zlecenie: no ZUS, no health,
 *                       PIT-0 → net = gross (§3.2)
 *  - b2b              — samozatrudnienie: cost = invoice net, the contractor
 *                       settles their own ZUS/PIT (§4)
 *  - powolanie        — board member by resolution: health 9% + PIT only (§5)
 *  - dzielo           — umowa o dzieło: no ZUS, no health; PIT 12/32 on
 *                       (gross − KUP 20%/50%); employer cost = gross
 *                       (verification doc §3; RUD reporting duty applies)
 */
export const PAYROLL_FORMS = ['uop', 'zlecenie', 'zlecenie_student', 'b2b', 'powolanie', 'dzielo'] as const;
export type PayrollForm = (typeof PAYROLL_FORMS)[number];

/** Maps an employee's EmploymentType to the payroll form; null = not derivable. */
export const EMPLOYMENT_TYPE_TO_PAYROLL_FORM: Record<EmploymentType, PayrollForm> = {
  employment_contract: 'uop',
  mandate_contract: 'zlecenie',
  mandate_contract_student: 'zlecenie_student',
  contractor: 'b2b',
  powolanie: 'powolanie',
  dzielo: 'dzielo',
};

export interface PayrollOptions {
  /** PIT-2 filed → kwota zmniejszająca 300 zł/mc (art. 31b PIT). Defaults: uop/powolanie true, zlecenie false. */
  pit2?: boolean;
  /** UOP KUP: 250 (default) or 300 (commuter, art. 22 ust. 2 pkt 3 PIT — employment only). */
  kup?: 250 | 300;
  /** Zlecenie/dzieło: 50% KUP (przeniesienie praw autorskich, art. 22 ust. 9 pkt 3 PIT) instead of 20%. */
  kupAutorskie?: boolean;
  /**
   * Dzieło only: contract concluded with one's OWN employer (or performed
   * for them via a third party) — the remuneration counts as employment
   * income for contributions (art. 8 ust. 2a + art. 18 ust. 1a ustawy o
   * sus): full ZUS + health on both sides, like an employment contract.
   * Default false.
   */
  ownEmployer?: boolean;
  /** PPK enrolled (employee 2% / employer 1.5% basic). Default false. */
  ppk?: boolean;
  /** PPK employee rate override (0.005–0.04). Default 0.02. */
  ppkEmployeeRate?: number;
  /** PPK employer rate override (0.015–0.04). Default 0.015. */
  ppkEmployerRate?: number;
  /** Wypadkowa rate — company-specific (0.0067–0.0333). Default 0.0167. */
  accidentRate?: number;
  /** Ulga dla młodych (<26, art. 21 ust. 1 pkt 148 PIT) — uop/zlecenie only. */
  under26?: boolean;
  /** Zlecenie: voluntary chorobowa (art. 11 sus). Default true. */
  sicknessVoluntary?: boolean;
  /** FP/FS exemption (55+/60+, parental-leave return). Default false. */
  fpExempt?: boolean;
  /** Cumulative taxable income (PIT base) this year BEFORE this month — 120 000 zł threshold tracking. */
  ytdIncomeBase?: number;
  /** Cumulative emerytalna/rentowa contribution base this year BEFORE this month — 30-krotność tracking. */
  ytdSocialBase?: number;
}

export interface PayrollEmployeeContributions {
  pension: number;    // emerytalna 9,76%
  disability: number; // rentowa 1,50%
  sickness: number;   // chorobowa 2,45%
  total: number;
}

export interface PayrollEmployerContributions {
  pension: number;    // emerytalna 9,76%
  disability: number; // rentowa 6,50%
  accident: number;   // wypadkowa (configurable)
  fpFs: number;       // FP 1,00% + FS 1,45%
  fgsp: number;       // FGŚP 0,10%
  ppk: number;        // employer PPK (when enrolled)
  total: number;
}

export interface PayrollBreakdown {
  form: PayrollForm;
  year: number;
  gross: number;
  /** null for b2b (contractor settles own ZUS) and zlecenie_student (no title). */
  employeeContributions: PayrollEmployeeContributions | null;
  /** Health 9% — null for b2b/zlecenie_student. */
  health: number | null;
  /** Koszty uzyskania przychodu applied this month; null when N/A. */
  kup: number | null;
  /** Monthly taxable base (rounded to full złoty); null when N/A. */
  taxBase: number | null;
  /** Monthly PIT advance (rounded to full złoty); null when N/A (b2b). */
  pitAdvance: number | null;
  /** Employee PPK part (deducted from net, after tax); null when PPK off/N/A. */
  ppkEmployee: number | null;
  net: number;
  /** null for b2b and powolanie (no employer-side contributions). */
  employerContributions: PayrollEmployerContributions | null;
  employerTotalCost: number;
  /** Caveats about defaults used (e.g. no YTD data — computed as for January). */
  assumptions: string[];
}

export interface PayrollCalcRequestDto {
  form: PayrollForm;
  /** Provide exactly one of gross / net. */
  gross?: number;
  net?: number;
  year?: number;
  options?: PayrollOptions;
}

/** GET /companies/:nip/employees/:id/payroll response. */
export interface EmployeePayrollResponse {
  employeeId: string;
  form: PayrollForm;
  year: number;
  month: number;
  /** Rate the gross was derived from. */
  rate: { rateType: RateType; amount: number };
  /** Hours used for an hourly rate (recorded or nominal); null for flat_monthly. */
  hours: number | null;
  breakdown: PayrollBreakdown;
}

// Auth / RBAC types
// 'hr' (Task HR1): company-scoped HR role — grants the 'hr' module and, via
// checkCompanyRole(user, nip, ['hr', ...]) on the HR endpoints, full HR
// visibility in that company (all employees, all absences incl. sick-leave
// letter codes, entitlement config). Line managers are NOT a role — manager
// scope derives from Employee.managerEmployeeId.
// 'crm' (spec 2026-09-09): company-scoped sales role — grants the 'crm'
// module and, via checkCompanyRole(user, nip, ['crm', 'admin', 'ksiegowy'])
// on the configuration endpoints, the right to reshape the pipeline. Since
// the CRM↔contractor unification (decision 2026-09-10) it ALSO opens the
// 'contractors' module — the full contractor list, because matching a CRM
// account to a counterparty by name requires seeing all of them — but with
// turnover HIDDEN: the money/count fields of ContractorListItem come back
// `null` unless the caller also has 'expense' or 'sales' (TURNOVER_MODULES).
// It still opens nothing in invoices, HR or fiscal.
export const USER_ROLE_NAMES = ['ksiegowy', 'admin', 'user', 'pracownik', 'hr', 'crm'] as const;
export type UserRoleName = (typeof USER_ROLE_NAMES)[number];

/**
 * What a company-scoped role opens, as data: `'all'` = every module, else the
 * listed modules. `user` is absent on purpose — its modules come from
 * user_module_grants. Typed as a Record over the role union so that adding a
 * role to USER_ROLE_NAMES without mapping it is a compile error, not a role
 * that silently opens nothing (how 'crm' shipped in v4.6.0).
 */
export const ROLE_MODULES: Record<Exclude<UserRoleName, 'user'>, 'all' | readonly PermissionModule[]> = {
  ksiegowy: 'all',
  admin: 'all',
  pracownik: ['hr'],
  // Task HR1: same module as pracownik; the elevated visibility is
  // role-gated at the HR endpoints via checkCompanyRole(user, nip, ['hr', …]).
  hr: ['hr'],
  // Contractors ride along since 2026-09-10 (CRM↔contractor unification):
  // the CRM needs the counterparty directory; turnover stays gated on
  // TURNOVER_MODULES.
  crm: ['crm', 'contractors'],
};

export const PERMISSION_MODULES = [
  'dashboard',
  'expense',
  'sales',
  'projects',
  'contractors',
  'mileage',
  'employees',
  'tags',
  'hr',
  'fiscal',
  'magazyn',
  'crm',
] as const;
export type PermissionModule = (typeof PERMISSION_MODULES)[number];

/** Modules that entitle a caller to see contractor turnover/invoice counts (decision 2026-09-10). */
export const TURNOVER_MODULES: readonly PermissionModule[] = ['expense', 'sales'];

// Authenticated user info returned by GET /auth/me (replaces group parsing on the frontend)
export interface AuthUserInfo {
  id: string;
  email: string;
  displayName: string | null;
  provider: 'entra' | 'local';
}

// Per-company access summary: roles held + module access (full or module-restricted)
// + optional tag-level filters for the 'expense'/'sales' modules when access is tag-restricted.
export interface CompanyAccess {
  roles: UserRoleName[];
  modules: PermissionModule[] | 'all';
  tagFilters?: Partial<Record<'expense' | 'sales', string[]>>;
}

// Resolved permission set for a user: superadmin bypasses all per-company checks.
export interface UserPermissions {
  isSuperAdmin: boolean;
  companies: Record<string, CompanyAccess>; // keyed by company NIP
}

// Per-user API token for POST /invoice/upload (GET /auth/api-token).
// The plaintext is never retrievable after creation — only the display prefix.
export interface ApiTokenStatus {
  exists: boolean;
  tokenPrefix?: string;
  createdAt?: string; // ISO
  lastUsedAt?: string | null; // ISO
}

// POST /auth/api-token response — `token` is the plaintext, shown exactly once.
export interface ApiTokenCreated {
  token: string;
  tokenPrefix: string;
  createdAt: string; // ISO
}

// ==================== NBP FX RATES ====================

// One currency's current rate + trend, as served by GET /fx/rates for the
// dashboard widget. Not company-scoped — NBP rates are public data.
export interface FxRateWidgetEntry {
  rate: number;
  effectiveDate: string; // YYYY-MM-DD, the NBP publication date of `rate`
  /** Latest rate minus the oldest rate inside the requested window (4dp). */
  change30d: number;
  series: Array<{ date: string; rate: number }>;
}

// ==================== HR: ABSENCES & LEAVE BALANCES (Task HR2) ====================
// Legal reference: docs/superpowers/research/pl-hr-leave-law.md.
// Balances are hour-accounted (1 day = 8 h regardless of etat, art. 154² §2 KP);
// `*Days` fields are derived displays (hours / dailyNormHours) and may be fractional.

export const ABSENCE_TYPES = [
  'vacation',              // urlop wypoczynkowy (art. 152–154 KP) — consumes the pool
  'vacation_on_demand',    // urlop na żądanie (art. 167² KP) — pool + 4 days/yr cap
  'unpaid',                // urlop bezpłatny (art. 174 KP) — no pool, no cap
  'occasional',            // urlop okolicznościowy (§15 rozp. MPiPS 15.05.1996) — 1–2 days/event
  'childcare_art188',      // opieka nad dzieckiem (art. 188 KP) — 2 days/yr, extra
  'care_art173',           // urlop opiekuńczy (art. 173¹ KP) — 5 days/yr, unpaid, extra
  'force_majeure_art148',  // siła wyższa (art. 148¹ KP) — 2 days/yr @50%, extra
  'sick',                  // e-ZLA — recorded by HR (Task HR3), never requested
  'unavailability_b2b',    // non-UOP "niedostępność" — notify-only, never "urlop"
  'remote_work',           // informational only, no pool
] as const;
export type AbsenceType = (typeof ABSENCE_TYPES)[number];

export const ABSENCE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export type AbsenceStatus = (typeof ABSENCE_STATUSES)[number];

export interface AbsenceListItem {
  id: string;
  companyNip: string;
  employeeId: string;
  employeeName: string;
  type: AbsenceType;
  status: AbsenceStatus;
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
  /** Pool cost in hours (0-cost for weekends/holidays; informational for non-pool types). */
  hoursTotal: number;
  /** hoursTotal / dailyNormHours — display helper, may be fractional (half-days). */
  days: number;
  halfDayStart: boolean;
  halfDayEnd: boolean;
  /** Request reason. Never present in the calendar for non-HR callers. */
  reason: string | null;
  submittedByUserId: string;
  decidedByUserId: string | null;
  decidedAt: string | null; // ISO datetime
  decisionNote: string | null;
  cancelledAt: string | null; // ISO datetime
  createdAt: string; // ISO datetime
}

export interface SubmitAbsenceDto {
  /** Defaults to the caller's own employee record; submitting for someone else requires hr/admin. */
  employeeId?: string;
  type: AbsenceType;
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD (same calendar year as dateFrom)
  halfDayStart?: boolean;
  halfDayEnd?: boolean;
  reason?: string;
}

export interface AbsenceBalancePreview {
  year: number;
  before: { remainingHours: number; remainingDays: number };
  after: { remainingHours: number; remainingDays: number };
}

export interface SubmitAbsenceResponse {
  absence: AbsenceListItem;
  /** null for non-UOP employees (no statutory pool). Unchanged before/after for non-pool types. */
  balancePreview: AbsenceBalancePreview | null;
}

export interface DecideAbsenceDto {
  note?: string;
}

export interface AbsenceFilterParams {
  employeeId?: string;
  from?: string;   // YYYY-MM-DD — absences ending on/after
  to?: string;     // YYYY-MM-DD — absences starting on/before
  status?: AbsenceStatus;
  type?: AbsenceType;
}

// GET /companies/:nip/employees/:id/leave-balance?year=
export interface LeaveBalanceResponse {
  employeeId: string;
  year: number;
  entitlementDays: number;   // baseHours / norm
  carriedOverDays: number;   // carry-over from the previous year (urlop zaległy, art. 168 KP)
  adjustmentDays: number;    // manual HR correction (art. 158 / art. 155² …)
  usedDays: number;          // approved pool absences this year
  pendingDays: number;       // pool absences awaiting approval
  remainingDays: number;
  remainingHours: number;
  onDemandUsedDays: number;  // na żądanie used (pending+approved) — capped at 4/yr, art. 167² KP
  /** Carry-over still unconsumed (FIFO: usage eats the oldest bucket first) + its art. 168 deadline. */
  zalegly: { days: number; deadline: string };
}

export type AbsenceCalendarScope = 'me' | 'team' | 'company';

export interface AbsenceCalendarEntry {
  id: string;
  /** Sick absences keep type 'sick' but carry no detail for non-HR callers. */
  type: AbsenceType;
  status: AbsenceStatus; // pending | approved only
  dateFrom: string;
  dateTo: string;
  halfDayStart: boolean;
  halfDayEnd: boolean;
  /** HR/admin only — always null for other callers (incl. the employee's manager). */
  reason: string | null;
  /** HR/admin only — letter code A–E + care variant; always null for other callers. */
  sickLeave: { letterCode: string | null; careFor: string | null } | null;
}

export interface AbsenceCalendarRow {
  employeeId: string;
  firstName: string;
  lastName: string;
  department: string | null;
  entries: AbsenceCalendarEntry[];
}

// ==================== HR: SICK LEAVE / e-ZLA (Task HR3) ====================
// Legal reference: docs/superpowers/research/pue-zus-ezla-and-ux.md PART 1.
// The employer NEVER receives the ICD-10 diagnosis (art. 55 ustawy
// zasilkowej) — only the period, the letter code A-E, and own-illness vs
// care-of-family-member. sick_leaves has no diagnosis column at all; these
// types mirror that constraint by never exposing more than letterCode/careFor.

export const SICK_LEAVE_LETTER_CODES = ['A', 'B', 'C', 'D', 'E'] as const;
export type SickLeaveLetterCode = (typeof SICK_LEAVE_LETTER_CODES)[number];

export const SICK_LEAVE_CARE_FOR = ['self', 'child', 'family'] as const;
export type SickLeaveCareFor = (typeof SICK_LEAVE_CARE_FOR)[number];

export const SICK_LEAVE_SOURCES = ['manual', 'pue_import'] as const;
export type SickLeaveSource = (typeof SICK_LEAVE_SOURCES)[number];

// POST /companies/:nip/sick-leaves (hr/admin/superadmin only)
export interface CreateSickLeaveDto {
  employeeId: string;
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
  seriesNumber?: string;
  letterCode?: SickLeaveLetterCode;
  /** Defaults to 'self' when omitted. */
  careFor?: SickLeaveCareFor;
}

/** A vacation absence trimmed/cancelled by art. 166 KP (sickness interrupts vacation). */
export interface SickLeaveVacationSplit {
  absenceId: string;
  action: 'trimmed' | 'split' | 'cancelled';
  /** The (up to two) resulting vacation ranges after the split; empty when cancelled. */
  resultingRanges: Array<{ absenceId: string; dateFrom: string; dateTo: string }>;
}

export interface SickLeaveConflict {
  id: string;
  type: AbsenceType;
  status: AbsenceStatus;
  dateFrom: string;
  dateTo: string;
}

export interface SickLeaveListItem {
  id: string; // sick_leaves.id
  absenceId: string;
  employeeId: string;
  employeeName: string;
  dateFrom: string;
  dateTo: string;
  days: number;
  status: AbsenceStatus;
  seriesNumber: string | null;
  letterCode: SickLeaveLetterCode | null;
  careFor: SickLeaveCareFor | null;
  source: SickLeaveSource;
  importedAt: string | null; // ISO datetime
}

export interface CreateSickLeaveResponse {
  sickLeave: SickLeaveListItem;
  /** Vacation absences trimmed/split/cancelled by art. 166 (empty when there was no overlap). */
  vacationSplits: SickLeaveVacationSplit[];
}

/** Returned (409) when overlapping approved vacation exists and `?override=true` was not passed. */
export interface SickLeaveOverlapWarning {
  message: string;
  conflicts: SickLeaveConflict[];
}

// GET /companies/:nip/sick-leaves?from=&to= (hr/admin only)
export interface SickLeaveRegisterParams {
  from?: string;
  to?: string;
}

// ---- PUE / eZUS report import (Task HR3) ----
// Two-phase: dryRun=true (default) previews matching; dryRun=false commits the
// caller-confirmed rows. Matching: PESEL first, then lastName+firstName+dateOfBirth.
// Idempotent on (employeeId, dateFrom, dateTo, seriesNumber).

export type SickLeaveImportMatchType = 'pesel' | 'name_dob' | 'none';

export interface SickLeaveImportRow {
  /** 0-based index into the parsed data rows — the id used to confirm a row on commit. */
  rowIndex: number;
  pesel: string | null;
  lastName: string | null;
  firstName: string | null;
  dateOfBirth: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  seriesNumber: string | null;
  letterCode: SickLeaveLetterCode | null;
  matchedEmployeeId: string | null;
  matchedEmployeeName: string | null;
  matchType: SickLeaveImportMatchType;
  /** Already imported (same employeeId/dateFrom/dateTo/seriesNumber) — auto-skipped on commit. */
  alreadyImported: boolean;
  /** Overlapping approved vacation on the matched employee (art. 166) — needs confirmation to commit. */
  conflict: SickLeaveConflict | null;
  /** Row-level parse problem (bad date, invalid letter code, …) — row is never auto-committed. */
  error: string | null;
}

export interface SickLeaveImportPreviewResponse {
  totalRows: number;
  columnMapping: Record<string, string>;
  matched: SickLeaveImportRow[];
  unmatched: SickLeaveImportRow[];
  conflicts: SickLeaveImportRow[];
}

export interface SickLeaveImportCommitResult {
  rowIndex: number;
  outcome: 'created' | 'skipped_duplicate' | 'skipped_error' | 'skipped_not_found';
  sickLeave?: SickLeaveListItem;
  vacationSplits?: SickLeaveVacationSplit[];
  message?: string;
}

export interface SickLeaveImportCommitResponse {
  results: SickLeaveImportCommitResult[];
}

// ==================== HR: UI SUPPORT (Task HR4) ====================

/**
 * GET /companies/:nip/hr/me — the caller's HR context within one company.
 * `isManager` is the UI's "am I a manager" signal (drives the Team surface);
 * it mirrors the backend's manager derivation (Employee.managerEmployeeId,
 * direct reports only) so the frontend never has to guess from calendar 403s.
 */
export interface HrMeResponse {
  /** The caller's Employee record in this company; null when not linked (e.g. pure admin accounts). */
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    employmentType: EmploymentType | null;
    dailyNormHours: number;
    department: string | null;
    /** Employee.contractorId is set — unlocks the B2B "Moje faktury" tab. */
    hasContractor: boolean;
  } | null;
  /** True when at least one employee reports to the caller in this company. */
  isManager: boolean;
  directReportsCount: number;
  /** superadmin, or role 'hr'/'admin' in this company — unlocks the HR/admin surface. */
  isHrAdmin: boolean;
}

// GET /companies/:nip/hr/holidays?from=&to= — Polish public holidays (global data, JWT-gated).
export interface HolidayItem {
  date: string; // YYYY-MM-DD
  name: string;
}

/** GET /companies/:nip/hr/entitlements?year= — one ewidencja urlopowa row (hr/admin only). */
export interface LeaveRegisterRow {
  employeeId: string;
  firstName: string;
  lastName: string;
  department: string | null;
  employmentType: EmploymentType | null;
  fte: number;
  leaveSeniorityYears: number | null;
  firstJobEver: boolean;
  dailyNormHours: number;
  /** null for non-UOP employees and unset contract types (no statutory pool). */
  balance: LeaveBalanceResponse | null;
}

/**
 * PUT /companies/:nip/hr/entitlements/:employeeId (hr/admin only) — edits the
 * entitlement inputs (Employee.fte/leaveSeniorityYears/firstJobEver) and the
 * year's manual adjustment. Changing an input recomputes the year's baseHours
 * from the entitlement engine (art. 153–155 KP).
 */
export interface UpdateLeaveConfigDto {
  year: number;
  fte?: number;
  leaveSeniorityYears?: number | null;
  firstJobEver?: boolean;
  /** Replaces the year's manual adjustment, in days at the employee's daily norm (may be negative). */
  adjustmentDays?: number;
  note?: string;
}

export type FxRatesResponse = Record<string, FxRateWidgetEntry>; // keyed by ISO currency code

// ===== HR compliance (deadlines & reminder e-mails) =====
// Requirements derive from Employee.employmentType:
//  * mandate_contract_student → student_card (ZUS exemption proof — art. 6
//    ust. 4 ustawy o sus: student <26 with a valid legitymacja);
//  * employment_contract → medical_exam (art. 229 KP) + bhp_training
//    (art. 237³ KP), plus contract_expiry while contractExpiryDate is set
//    (reminder-only track, no document upload);
//  * dzielo → rud_report (ZUS RUD notification within 7 days of conclusion —
//    reminder-only track, HR marks it done);
//  * mandate_contract / contractor / powolanie / null → nothing.

export const COMPLIANCE_TYPES = [
  'student_card',
  'medical_exam',
  'bhp_training',
  'contract_expiry',
  // Zgłoszenie umowy o dzieło do ZUS na formularzu RUD — 7 dni od zawarcia
  // (art. 36 ust. 17 ustawy o sus, obowiązek od 1.01.2021). Informational,
  // reminder-only, HR-facing one-off track: anchored on contractStartDate +
  // 7 days, reminded at +3 days and on the deadline, marked done by HR
  // (status 'valid'). No RUD is due for a dzieło with one's own employee /
  // performed for one's own employer (those are contribution-covered
  // instead — the track still shows and HR simply marks it done).
  'rud_report',
] as const;
export type ComplianceType = (typeof COMPLIANCE_TYPES)[number];

/**
 * Employer-ordered tracks (medical exam — art. 229 KP, BHP training —
 * art. 237³ KP): the employee never uploads these. Only hr / company admin /
 * superadmin records the document, and the submission is auto-verified
 * (straight to 'valid', no manager round-trip). The employee just sees the
 * validity date.
 */
export const EMPLOYER_MANAGED_COMPLIANCE_TYPES: readonly ComplianceType[] = [
  'medical_exam',
  'bhp_training',
];

/**
 * Document types selectable in the kartoteka upload dialog that auto-link
 * to the employee's compliance requirement row (marked valid, expiry
 * tracked). Uploading via the kartoteka is a back-office act, so the link
 * is trusted like an employer-recorded compliance document.
 */
export const DOCUMENT_COMPLIANCE_TYPES = [
  'medical_exam',
  'bhp_training',
  'student_card',
] as const;
export type DocumentComplianceType = (typeof DOCUMENT_COMPLIANCE_TYPES)[number];

export const COMPLIANCE_STATUSES = [
  'missing',
  'pending_verification',
  'valid',
  'expired',
  'rejected',
] as const;
export type ComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];

/** Row of GET /companies/:nip/hr/compliance (scoped: own / direct reports / whole company). */
export interface ComplianceItem {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string | null;
  type: ComplianceType;
  status: ComplianceStatus;
  /** YYYY-MM-DD expiry of the current (or proposed, while pending) document. */
  validUntil: string | null;
  /** Days until validUntil (negative = already past); null when validUntil is null. */
  daysLeft: number | null;
  documentId: string | null;
  documentFileName: string | null;
  rejectedReason: string | null;
  note: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
  /** Caller is this employee (drives the self-service upload UI). */
  isSelf: boolean;
  /** Caller may verify/reject this row (direct manager, hr, company admin, superadmin). */
  canVerify: boolean;
}

export interface ComplianceListParams {
  status?: ComplianceStatus;
  type?: ComplianceType;
}

// ===== In-app notifications (navbar bell) =====
// Texts are rendered client-side from `type` + `payload` (i18n) — the DB
// stores no language-specific strings.

export const NOTIFICATION_TYPES = [
  // B2B contractor asks for an earlier transfer for one of their invoices.
  // payload: { employeeName, invoiceNumber, requestedDate, note?, gross?, currency? }
  'early_payment_request',
  // Employee flags that money never arrived. With an invoice (B2B) the
  // payload carries invoiceNumber/gross/currency; the general pay alarm
  // (other contract types) carries just employeeName.
  // payload: { employeeName, invoiceNumber?, gross?, currency? }
  'funds_not_received',
  // Employee requests a reimbursement for a privately paid invoice.
  // payload: { employeeName, invoiceNumber, amountPln }
  'reimbursement_requested',
  // Decision on the employee's reimbursement request.
  // payload: { invoiceNumber, amountPln, reason? }
  'reimbursement_approved',
  'reimbursement_rejected',
  // "Moje dane" reveal hard-locked after 5 failed PESEL attempts — a
  // possible intrusion signal. payload: { employeeName }
  'my_data_locked',
  // A B2B employee's contractor failed government-registry verification
  // (account off the White List, negative VAT status, or the API being
  // unavailable). Warning copy only — processing is NEVER blocked.
  // payload: { employeeName, contractorName, problem, invoiceNumber?, vatStatus? }
  'b2b_verification_problem',
  // A document was published to the employee (documents plan 3).
  // payload: { title, number, deadline? }
  'document_published',
  // Nightly reminder for a still-pending electronic acceptance: 3 days
  // before the deadline and again on deadline day (deduped on lastRemindedAt).
  // payload: { title, number, deadline }
  'document_acceptance_reminder',
  // Day after the deadline, still pending: employee + the financial circle
  // (admin/ksiegowy + manager). payload: { title, number, deadline, employeeName }
  'document_overdue',
  // A signed copy came back (QR/token-routed inbound e-mail or upload) and
  // waits for HR confirmation (documents plan 4). Recipients: the company's
  // active admin+hr role holders. payload: { title, fileName }
  'document_returned',
  // An employee filed an absence/unavailability request. Recipients: the
  // employee's direct manager (linked user), falling back to hr/admin role
  // holders. Notify-only types (unavailability_b2b, remote_work) arrive
  // already auto-approved — status tells the renderer which wording to use.
  // payload: { employeeName, absenceType, dateFrom, dateTo, status }
  'absence_submitted',
  // A fiscal (KSeF) invoice job ended REJECTED after exhausting retries.
  // payload: { uuid, number?, error? }  (error = "CODE: message")
  'fiscal_invoice_rejected',
  // A fiscal receipt (e-paragon/paper) job ended REJECTED.
  // payload: { uuid, printerName, error? }
  'fiscal_receipt_rejected',
  // A fiscal printer missed its heartbeat window and is considered offline.
  // payload: { printerName, lastHeartbeatAt? }
  'fiscal_printer_offline',
  // A printer's scheduled daily (fiscal) report job failed.
  // payload: { printerName, date, error? }
  'fiscal_daily_report_failed',
  // Safety-net detector (cron, from 06:00 Warsaw): a printer's fiscal day was
  // never closed — confirmed sales exist for a PRIOR day with no daily report
  // after them. Fired once per missed day per printer record (a printer shared
  // across companies is one record per company → each company's admins get
  // their own alert). payload: { printerCode, printerName, missedDay }
  'fiscal_daily_report_overdue',
  // A representative countersign turned 'pending' — the contract waits for
  // that representative's signature (the employee already signed, umowa spec
  // 2026-07-23). Recipients: the countersign's linked user, else the
  // company's active admin+hr role holders.
  // payload: { title, number, employeeName, representativeName }
  'document_countersign_pending',
  // Every countersign is signed — the contract is fully executed.
  // Recipients: the company's active admin+ksiegowy role holders (accounting).
  // payload: { title, number, employeeName }
  'document_fully_signed',
  // A signed copy came back for an ALREADY completed acceptance and was a
  // provably newer revision of the stored file (same bytes + an extra valid
  // qualified/trusted-profile signature — e.g. a representative countersigned
  // the PDF after the employee's signature had already confirmed it). The
  // stored pointer moved to the new revision; the old object is KEPT.
  // Recipients: the company's active admin+hr role holders.
  // payload: { title, fileName, signerName? }
  'document_return_superseded',
  // A signed copy came back for an ALREADY completed acceptance and could NOT
  // be proven to be a newer revision (re-scan/re-save, a missing or broken
  // signature, an older copy…). NOTHING was changed — the file sits in an
  // `unapplied/` quarantine key and a human must look at it.
  // Recipients: the company's active admin+hr role holders.
  // payload: { title, fileName, reason }
  'document_return_needs_review',
  // A return token MATCHED a row (so the file is certainly a signed document
  // return, never an invoice) but storing it FAILED — the stored object was
  // missing, S3 hiccuped, the database write failed. Nothing was applied and
  // the bytes are gone; the sender must re-send or someone must upload the
  // file by hand. Recipients: admin+hr+ksiegowy, falling back to any user
  // with company access and then to Entra administrators.
  // payload: { title, fileName, error }
  'document_return_failed',
  // A NEW lead arrived through the public API (never a replay, never a
  // manual panel entry). Recipients: the company's active 'crm' role holders
  // (bell + e-mail) and 'admin' role holders (bell only), deduplicated.
  // link: /:nip/crm?lead=<leadId>. displayName = "first last" | company |
  // e-mail | phone; preview = messagePreview(message) (<=160 chars).
  // payload: { leadId, sourceName, formKey, displayName, email, phone, companyName, preview, status }
  'crm_lead_received',
  // A contractor document (umowa/NDA/pełnomocnictwo…) with an expiry date is
  // running out: 30 days before, 7 days before and on/after the expiry day —
  // each stage exactly once per document (contractor_documents.
  // last_reminder_stage). Recipients: the company's active 'crm' role holders
  // (bell + e-mail) and 'admin' role holders (bell only), disjoint — the same
  // circle as crm_lead_received. link: /:nip/crm/klienci/<accountId>?tab=documents
  // when the contractor has a CRM account, else
  // /:nip/contractors/<contractorId>?tab=documents — both branches deep-link
  // straight to the documents tab (frontend `useTabParam`).
  // label = document label ?? fileName; contractorName may be null (a
  // NIP-less counterparty without a name); daysLeft is 0 on the expiry day
  // and negative afterwards; stage is 'pre30' | 'pre7' | 'expired'.
  // payload: { contractorId, contractorName, documentId, label, docType, expiryDate, daysLeft, stage, accountId }
  'contractor_document_expiring',
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface NotificationItem {
  id: string;
  type: NotificationType;
  payload: Record<string, string | number | null>;
  /** In-app path to open on click (e.g. an invoice detail), if any. */
  link: string | null;
  createdAt: string; // ISO
  readAt: string | null; // ISO
}

/** GET /companies/:nip/notifications */
export interface NotificationsResponse {
  items: NotificationItem[];
  unreadCount: number;
  total: number;
  page: number;
  pageSize: number;
}

/** POST /companies/:nip/notifications/read — ids XOR all. */
export interface MarkNotificationsReadDto {
  ids?: string[];
  all?: boolean;
}

// ===== B2B "Moje faktury" (HR self-service) =====

/** Employee-facing payment progress, mapped from invoice.status:
 *  unpaid → received, exported → passed_for_payment, paid → paid. */
export const MY_INVOICE_STATUSES = ['received', 'passed_for_payment', 'paid'] as const;
export type MyInvoiceStatus = (typeof MY_INVOICE_STATUSES)[number];

/** Row of GET /companies/:nip/hr/my-invoices (the caller-contractor's own purchase invoices). */
export interface MyInvoiceItem {
  invoiceId: string;
  invoiceNumber: string | null;
  /** YYYY-MM-DD — when the invoice reached the company (emailDate, else ingest date). */
  receivedDate: string;
  /** YYYY-MM-DD — receivedDate + 14 days (the contractual payment term). */
  dueDate: string;
  gross: number | null;
  currency: string | null;
  status: MyInvoiceStatus;
}

export interface EarlyPaymentRequestDto {
  /** YYYY-MM-DD — the transfer date the contractor asks for. */
  requestedDate: string;
  note?: string;
}

// ===== HR "Moje dane" (read-only self view, PESEL-gated sensitive data) =====

/** GET /companies/:nip/hr/my-data — plain sections + masked placeholders.
 *  Sensitive VALUES never travel in this response. */
export interface MyDataResponse {
  personal: {
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
    sex: Sex | null;
  };
  contact: {
    privateEmail: string | null;
    privatePhone: string | null;
    workEmail: string | null;
  };
  address: {
    street: string | null;
    postalCode: string | null;
    city: string | null;
    country: string | null;
  };
  employment: {
    employmentType: EmploymentType | null;
    contractStartDate: string | null;
    contractExpiryDate: string | null;
    jobTitle: string | null;
    department: string | null;
    managerName: string | null;
    fte: number;
    dailyNormHours: number;
    taxOffice: string | null;
    nfzBranch: string | null;
  };
  sensitive: {
    /** Fully masked (its ending is the verification code) — '•••••••••••' or null when absent. */
    peselMasked: string | null;
    idDocumentType: IdDocumentType | null;
    idDocumentNumberMasked: string | null;
    /** '•••• 1234' — only the tail is shown; null when absent. */
    ibanMasked: string | null;
  };
  /** False → the reveal button is disabled ("skontaktuj się z HR"). */
  hasPesel: boolean;
  /** Hard brute-force lock (5 failed attempts) — only hr/admin unlocks. */
  revealLocked: boolean;
}

/** POST /companies/:nip/hr/my-data/reveal {peselLast4} — full values after verification. */
export interface MyDataRevealResponse {
  pesel: string | null;
  idDocumentType: IdDocumentType | null;
  idDocumentNumber: string | null;
  privateIban: string | null;
  /** Monthly payroll breakdown; null when the engine cannot compute (e.g. no rate). */
  payroll: EmployeePayrollResponse | null;
}

// ===== Employee expense reimbursements (zwroty za FV) =====

export const REIMBURSEMENT_STATUSES = [
  'pending_approval',
  'approved',
  'rejected',
  'exported',
] as const;
export type ReimbursementStatus = (typeof REIMBURSEMENT_STATUSES)[number];

// Cost-list "Zwroty" filter: 'with'/'without' key off the ACTIVE (non-rejected)
// case; a concrete status narrows to that status.
export type ReimbursementFilter =
  | 'all'
  | 'with'
  | 'without'
  | Exclude<ReimbursementStatus, 'rejected'>;

/** Candidate row of POST /hr/reimbursements/search. */
export interface ReimbursementCandidate {
  invoiceId: string;
  invoiceNumber: string | null;
  vendorName: string | null;
  issueDate: string | null;
  gross: number | null;
  currency: string | null;
  /** The PLN amount that would be reimbursed (gross or the statutory NBP conversion); null = conversion missing. */
  amountPln: number | null;
}

/** Search: Polish path (nip + gross) OR fallback (issueDate + gross). */
export interface ReimbursementSearchDto {
  nip?: string;
  issueDate?: string; // YYYY-MM-DD
  gross: number;
}

export interface ReimbursementItem {
  id: string;
  invoiceId: string;
  invoiceNumber: string | null;
  vendorName: string | null;
  employeeId: string;
  employeeName: string;
  amountPln: number;
  iban: string;
  status: ReimbursementStatus;
  requestedAt: string; // ISO
  decidedAt: string | null;
  rejectedReason: string | null;
  exportedAt: string | null;
}

/** Payment record for the mBank export view ("Zwroty dla pracowników" section). */
export interface ReimbursementExportRecord {
  reimbursementId: string;
  employeeName: string;
  /** Kartoteka city — goes into the PAIN creditor postal address (TwnNm). */
  employeeCity: string | null;
  iban: string;
  amountPln: number;
  invoiceNumber: string | null;
  /** Transfer title, e.g. "Zwrot za FV/Paragon {number}". */
  title: string;
}

// ---------------------------------------------------------------------------
// Document templates (spec 2026-07-16 §3.3/§3.4): per-company markdown
// templates with {{variables}}, immutable published versions.
// ---------------------------------------------------------------------------

export const DOCUMENT_TEMPLATE_CATEGORIES = ['employee', 'b2b', 'contractor'] as const;
export type DocumentTemplateCategory = (typeof DOCUMENT_TEMPLATE_CATEGORIES)[number];

export const DOCUMENT_VISIBILITIES = ['public', 'private'] as const;
export type DocumentVisibility = (typeof DOCUMENT_VISIBILITIES)[number];

export const DOCUMENT_AUDIENCES = ['all', 'uop', 'uz', 'b2b'] as const;
export type DocumentAudience = (typeof DOCUMENT_AUDIENCES)[number];

export const DOCUMENT_ACCEPTANCE_MODES = ['none', 'electronic', 'physical'] as const;
export type DocumentAcceptanceMode = (typeof DOCUMENT_ACCEPTANCE_MODES)[number];

/** null/'standard' = plain template; 'umowa_zlecenie' = requires contract
 *  data (dates, hourly rate, representatives) at publication time. */
export const DOCUMENT_TEMPLATE_KINDS = ['standard', 'umowa_zlecenie'] as const;
export type DocumentTemplateKind = (typeof DOCUMENT_TEMPLATE_KINDS)[number];

export interface DocumentTemplateListItem {
  id: string;
  category: DocumentTemplateCategory;
  name: string;
  description: string | null;
  visibility: DocumentVisibility;
  audience: DocumentAudience;
  acceptanceMode: DocumentAcceptanceMode;
  defaultDeadlineDays: number | null;
  includeLogo: boolean;
  templateKind?: DocumentTemplateKind | null;
  currentVersion: number;
  archivedAt: string | null;
  updatedAt: string;
}

/** Adds the DRAFT markdown content — never touched by publish-version. */
export interface DocumentTemplateDetail extends DocumentTemplateListItem {
  contentMd: string;
}

export interface DocumentTemplateVersionInfo {
  id: string;
  version: number;
  publishedAt: string;
  publishedByUserId: string | null;
}

export interface CreateDocumentTemplateDto {
  category: DocumentTemplateCategory;
  name: string;
  description?: string;
}

export interface UpdateDocumentTemplateDto {
  name?: string;
  description?: string | null;
  contentMd?: string;
  visibility?: DocumentVisibility;
  audience?: DocumentAudience;
  acceptanceMode?: DocumentAcceptanceMode;
  defaultDeadlineDays?: number | null;
  includeLogo?: boolean;
  templateKind?: DocumentTemplateKind | null;
}

// ---------------------------------------------------------------------------
// Document publications + acceptances (spec 2026-07-16 §3.5/§3.6): publish a
// template version to an employee audience ('campaign') or one employee
// ('individual'); track per-employee acceptance (electronic PESEL confirmation
// or physical scan upload).
// ---------------------------------------------------------------------------

export const DOCUMENT_PUBLICATION_KINDS = ['campaign', 'individual'] as const;
export type DocumentPublicationKind = (typeof DOCUMENT_PUBLICATION_KINDS)[number];

export const DOCUMENT_ACCEPTANCE_STATUSES = ['pending', 'returned', 'accepted', 'signed'] as const;
export type DocumentAcceptanceStatus = (typeof DOCUMENT_ACCEPTANCE_STATUSES)[number];

export interface DocumentPublicationListItem {
  id: string;
  number: string;
  title: string;
  kind: DocumentPublicationKind;
  audience: DocumentAudience | null;
  employeeId: string | null;
  employeeName: string | null;
  visibility: DocumentVisibility;
  acceptanceMode: DocumentAcceptanceMode;
  deadline: string | null;
  publishedAt: string;
  archivedAt: string | null;
  templateId: string;
  templateVersion: number;
  acceptanceTotal: number;
  acceptanceDone: number;
  /** `returned` acceptances awaiting HR confirmation ("do potwierdzenia"). */
  acceptanceReturned: number;
}

export interface DocumentAcceptanceRow {
  id: string;
  employeeId: string;
  employeeName: string;
  status: DocumentAcceptanceStatus;
  method: 'electronic' | 'physical' | null;
  acceptedAt: string | null;
  returnedAt: string | null;
  lastRemindedAt: string | null;
  /** Digital-signature check of the returned scan; null = not verified yet. */
  signatureVerification?: SignatureVerification | null;
  /**
   * A signed file is stored on this row (returned scan, mark-signed upload, or
   * — since 2026-09-05 — a digitally signed copy that came back by e-mail for
   * an ELECTRONICALLY accepted document). Drives download / attach-signed in
   * the publication sheet regardless of acceptance mode.
   */
  hasSignedFile?: boolean;
}

// Representative countersignatures (umowa spec 2026-07-23): after the
// employee's acceptance reaches its final state, the selected representatives
// sign sequentially — 'waiting' → 'pending' (their turn) → 'signed'.
export const DOCUMENT_COUNTERSIGN_STATUSES = ['waiting', 'pending', 'signed'] as const;
export type DocumentCountersignStatus = (typeof DOCUMENT_COUNTERSIGN_STATUSES)[number];

export interface DocumentCountersignRow {
  id: string;
  sequence: number;
  /** Snapshot taken at publication — representative rows are replace-all. */
  fullName: string;
  title: string;
  status: DocumentCountersignStatus;
  signedAt?: string | null;
  /** Digital-signature check of the uploaded file; null = not verified yet. */
  signatureVerification?: SignatureVerification | null;
  /** True when a signed file (scan or PAdES PDF) was uploaded. */
  hasFile: boolean;
  /** Linked user account allowed to sign this row (besides admin/hr). */
  userId?: string | null;
}

export interface DocumentPublicationDetail extends DocumentPublicationListItem {
  acceptances: DocumentAcceptanceRow[];
  /** Present (ordered by sequence) for umowa_zlecenie publications. */
  countersigns?: DocumentCountersignRow[];
}

/** Contract data required when publishing a templateKind='umowa_zlecenie'
 *  template. Dates are YYYY-MM-DD strings; formatting to DD.MM.YYYY happens
 *  only in the template variables. */
export interface PublicationContractInput {
  conclusionDate: string;
  dateFrom: string;
  dateTo: string;
  /** Gross hourly rate (PLN). */
  hourlyRate: number;
  /** CompanyRepresentative ids in signing order — exactly 1 ('one_person')
   *  or 2 ('two_person') per the company's representationMode. */
  representativeIds: string[];
  /** Default true — write the hourly rate into the employee's kartoteka
   *  (rateType 'hourly', validFrom = dateFrom). */
  writeRateToKartoteka?: boolean;
}

export interface CreatePublicationDto {
  templateId: string;
  kind: DocumentPublicationKind;
  audience?: DocumentAudience;
  employeeId?: string;
  deadline?: string; // YYYY-MM-DD
  /** Required (and only allowed) when the template is 'umowa_zlecenie'. */
  contract?: PublicationContractInput;
}

/** POST /companies/:nip/documents/publications response — the publication
 *  detail plus the outcome of the best-effort kartoteka rate write. */
export interface CreatePublicationResult extends DocumentPublicationDetail {
  /** True when the hourly rate was written to the employee's kartoteka. */
  rateWritten?: boolean;
  /** Set when the rate write failed (publication itself succeeded). */
  rateWarning?: string | null;
}

export interface HrDocumentItem {
  publicationId: string;
  acceptanceId: string | null;
  number: string;
  title: string;
  deadline: string | null;
  publishedAt: string;
  status: DocumentAcceptanceStatus | null;
  acceptanceMode: DocumentAcceptanceMode;
  acceptedAt: string | null;
}

export interface HrDocumentsResponse {
  pending: HrDocumentItem[];
  done: HrDocumentItem[];
  library: HrDocumentItem[];
}

// ===== FISCAL =====
// KSeF (Krajowy System e-Faktur) invoice fiscalization + fiscal receipts
// (e-paragon/paper via Novitus-style printers), absorbed from the standalone
// FSS-13-KSEF-Pod service. FiscalInvoiceInput/FiscalReceiptInput are the same
// JSON contract the pod accepted — ported field-for-field from its
// src/types.ts (InvoiceInput) and src/receipts/types.ts (ReceiptInput).

export const KSEF_ENVIRONMENTS = ['demo', 'test', 'production'] as const;
export type KsefEnvironment = (typeof KSEF_ENVIRONMENTS)[number];

export const FISCAL_INVOICE_STATUSES = ['QUEUED', 'PROCESSING', 'RETRY_WAIT', 'CONFIRMED', 'REJECTED'] as const;
export type FiscalInvoiceStatus = (typeof FISCAL_INVOICE_STATUSES)[number];

export const FISCAL_RECEIPT_STATUSES = ['QUEUED', 'DISPATCHED', 'CONFIRMED', 'REJECTED'] as const;
export type FiscalReceiptStatus = (typeof FISCAL_RECEIPT_STATUSES)[number];

// --- Invoice input (ported from FSS-13-KSEF-Pod src/types.ts) ---

export type FiscalVatRate = '23' | '8' | '5' | '0' | '0_WDT' | '0_EX' | 'zw' | 'np' | 'oo';

export interface FiscalBuyerIdentifier {
  type: 'nip' | 'vatUe' | 'none';
  value?: string; // nip: 10 cyfr; vatUe: np. "NL853682963B01" (prefiks kraju + numer); none: brak
}
export interface FiscalBuyerInput {
  name: string;
  identifier: FiscalBuyerIdentifier;
  address: { line1: string; line2?: string; countryCode: string }; // ISO-3166-1 alpha-2
}
export interface FiscalItemInput {
  name: string;           // P_7
  unit?: string;          // P_8A, np. "szt."
  quantity: number;       // P_8B
  // Cena jednostkowa — DOKŁADNIE JEDNO z dwóch pól (oba/żadne → 400):
  unitPriceNet?: number;   // P_9A, w walucie faktury; wartość pozycji → P_11 (netto)
  unitPriceGross?: number; // P_9B (> 0), w walucie faktury; wartość pozycji → P_11A (brutto),
                           // netto/VAT liczone metodą "w stu" — patrz fa3-builder computeTotals
  vatRate: FiscalVatRate; // P_12
  gtu?: string;           // "GTU_01".."GTU_13"
}
export interface FiscalExchangeRateInput { rate: number; table: string; date: string } // kurs NBP
export interface FiscalPaymentInput {
  method?: 'transfer' | 'cash' | 'card' | 'voucher' | 'check' | 'credit' | 'mobile';
  dueDate?: string;   // YYYY-MM-DD
  iban?: string;
}
export interface FiscalInvoiceInput {
  number: string;          // P_2
  issueDate: string;       // P_1, YYYY-MM-DD
  saleDate?: string;       // P_6
  currency: string;        // ISO-4217, np. PLN/EUR
  exchangeRate?: FiscalExchangeRateInput | undefined; // wymagany gdy currency != PLN i wystąpi VAT > 0
  exemptionBasis?: string; // wymagany gdy jakakolwiek pozycja ma vatRate 'zw' (podstawa prawna zwolnienia)
  buyer: FiscalBuyerInput;
  items: FiscalItemInput[];
  payment?: FiscalPaymentInput;
  remarks?: string;
  /**
   * Traktowanie nabywcy `identifier.type === 'vatUe'`:
   * - 'reverse_charge' (domyślne, zachowanie dotychczasowe): wszystkie pozycje wymuszane na 'np'
   *   (usługi / odwrotne obciążenie za granicą);
   * - 'explicit_rates': integrator sam podaje stawki — dostawa krajowa do podatnika UE
   *   (towar dostarczany w PL: 23/8/5) albo WDT ('0_WDT'); `vatRate` wymagany na każdej
   *   pozycji i nie jest weryfikowany względem rodzaju dostawy, VIES nadal weryfikowany,
   *   FA(3) nadal dostaje <KodUE>/<NrVatUE>.
   * Ignorowane dla `nip` i `none`.
   */
  vatUeMode?: 'reverse_charge' | 'explicit_rates';
}

// --- Receipt input (ported from FSS-13-KSEF-Pod src/receipts/types.ts) ---

export type FiscalReceiptVatRate = '23' | '8' | '5' | '0' | 'zw';
export type FiscalPaymentType = 'cash' | 'card' | 'transfer' | 'mobile' | 'voucher' | 'other';
export interface FiscalReceiptItemInput { name: string; quantity: number; unitPrice: number; vatRate: FiscalReceiptVatRate; unit?: string }
export interface FiscalReceiptPaymentInput { type: FiscalPaymentType; value: number; name?: string }
export interface FiscalReceiptInput {
  items: FiscalReceiptItemInput[];
  payments: FiscalReceiptPaymentInput[];
  buyerNip?: string;
  systemNumber?: string;
  footerLines?: string[];
}

// --- Settings / certificates ---

export interface FiscalCertificateInfo {
  environment: KsefEnvironment;
  fingerprint: string | null;
  expiry: string | null;
  format: 'p12' | 'pem' | null;
}

export interface FiscalSettingsDto {
  enabled: boolean;
  autoInvoiceNum: boolean;
  invoicePrefix: string | null;
  sellerName: string | null;
  sellerAddressLine1: string | null;
  sellerAddressLine2: string | null;
  sellerCountryCode: string;
  receiptLogoKey: string | null;
  environment: KsefEnvironment;
  certificates: FiscalCertificateInfo[];
}

// --- Printers ---

export interface FiscalPrinterDto {
  id: string;
  code: string;
  name: string;
  dailyReportMode: 'central' | 'agent';
  dailyReportTime: string;
  active: boolean;
  online: boolean;
  lastHeartbeatAt: string | null;
  deviceStatus: unknown;
  taxRates: Record<string, number> | null;
  uniqueNumber: string | null;
  lastDailyReport: string | null;
  queueDepth: number | null;
  protectedMemoryFree: number | null;
  agentVersion: string | null;
  blackout: boolean;
}

// --- API tokens (per-printer/integrator tokens on the fiscal module) ---

export interface FiscalApiTokenDto {
  id: string;
  name: string;
  tokenPrefix: string;
  /**
   * Allowed invoice-numbering prefixes for this token (per-service numbering
   * series). Empty = the token uses the company's fiscal_settings.invoicePrefix.
   * When a submit omits `numberingPrefix`, the FIRST entry is the default.
   */
  numberingPrefixes: string[];
  /**
   * KSeF issuance environment override for this token (application).
   * `null` = the token follows the company default
   * (fiscal_settings.environment). The resolved environment is FROZEN onto
   * each invoice at submit time. On PUT, `undefined` (field omitted) means
   * "leave unchanged" while an explicit `null` clears the override — a
   * name-only PUT never drops a production override.
   */
  environment: KsefEnvironment | null;
  active: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

// --- Invoice numbering counters (migration-continuity seeding) ---

/**
 * One `fiscal_invoice_numbering` row: the atomic per-(company, environment,
 * prefix, year, month) sequence counter used by auto-numbering. Admins can
 * seed `lastSeq` (PUT /companies/:nip/fiscal/numbering) when migrating from a
 * previous KSeF integration that already issued numbers for this series —
 * the counter can only move forward, never backward.
 */
export interface FiscalNumberingCounterDto {
  prefix: string;
  year: number;
  month: number;
  environment: KsefEnvironment;
  lastSeq: number;
}

/**
 * Envelope of a fiscal invoice submit (POST /fiscal/v1/:nip/invoices). Alongside
 * `uuid`/`callbackUrl`/`invoice`, the OPTIONAL top-level `numberingPrefix`
 * (NOT inside `invoice`) selects the per-service numbering series in
 * auto-numbering mode. Cascade: explicit `numberingPrefix` (must match
 * ^[A-Za-z0-9]{1,10}$ and be in the token's allowed list) → token's default
 * (first allowed prefix) → company fiscal_settings.invoicePrefix. Forbidden
 * (400) when auto-numbering is off.
 */
export interface FiscalInvoiceSubmitDto {
  uuid: string;
  callbackUrl?: string;
  numberingPrefix?: string;
  invoice: FiscalInvoiceInput;
}

// --- Stats dashboard ---

export interface FiscalStatsDto {
  invoices: {
    today: number;
    month: number;
    rejected: number;
    byDay: { date: string; confirmed: number; rejected: number }[];
  };
  receipts: {
    today: number;
    month: number;
    rejected: number;
    byDay: { date: string; confirmed: number; rejected: number }[];
  };
  printersOnline: number;
  printersTotal: number;
  environment: KsefEnvironment;
}

// ==================== Magazyn (warehouse) — WZ etap 1 ====================
// Spec: docs/superpowers/specs/2026-07-21-magazyn-wz-design.md

export const WZ_DELIVERY_METHODS = ['courier', 'seller_delivery', 'pickup'] as const;
export type WzDeliveryMethod = (typeof WZ_DELIVERY_METHODS)[number];

export const WZ_STATUSES = ['issued', 'returned'] as const;
export type WzStatus = (typeof WZ_STATUSES)[number];

/** Katalog Pozycji. unitPriceNet: TYLKO tutaj — nigdy w typach WZ. */
export interface WarehouseItem {
  id: string;
  code: string;
  color: string; // hex (#rrggbb), picker jak przy projektach
  name: string;
  projectId: string | null;
  projectName: string | null;  // denormalizowane do tabeli/etykiet
  projectColor: string | null;
  invoiceDescription: string | null;
  unitPriceNet: number | null;
  createdAt: string;
  updatedAt: string;
}
export interface CreateWarehouseItemDto {
  code: string;
  color: string;
  name: string;
  projectId?: string | null;
  invoiceDescription?: string | null;
  unitPriceNet?: number | null;
}
export type UpdateWarehouseItemDto = Partial<CreateWarehouseItemDto>;

export interface WzLineInput {
  itemId?: string | null;      // informacyjna referencja do katalogu
  code: string;                // Kod Towaru (snapshot)
  description?: string | null; // Opis / Uwagi
  unit?: string;               // j.m., default 'szt.'
  quantity: number;            // > 0
}
export interface WzLine extends Required<Omit<WzLineInput, 'itemId'>> {
  id: string;
  ordinal: number;
  itemId: string | null;
  description: string | null;
}

export interface WzListItem {
  id: string;
  number: string;              // WZ/001/07/2026
  issueDate: string;           // YYYY-MM-DD
  contractorId: string;
  contractorName: string;
  deliveryMethod: WzDeliveryMethod;
  trackingNumber: string | null;
  status: WzStatus;
  createdAt: string;
}
export interface WzDetail extends WzListItem {
  notes: string | null;
  templateVersion: string;
  contractorDocumentId: string;
  returnedAt: string | null;
  lines: WzLine[];
}
export interface CreateWzDto {
  issueDate?: string;                    // default: dziś (Warszawa)
  contractorId: string;
  deliveryMethod: WzDeliveryMethod;
  trackingNumber?: string | null;        // wymagany gdy 'courier'
  notes?: string | null;
  lines: WzLineInput[];                  // min 1
}
export type UpdateWzDto = CreateWzDto;

export interface WzListParams {
  from?: string; to?: string;            // issueDate YYYY-MM-DD
  contractorId?: string;
  status?: WzStatus;
  search?: string;                       // number / contractorName / kod
  page?: number; pageSize?: number;
}

export interface UploadSignedWzResult {
  matched: boolean;
  wzId: string | null;
  wzNumber: string | null;
  attachmentCreated: boolean;            // powstał wpis 'wz_zalacznik'
}

// ============ Global API applications (pap_… tokens) ============

/** Canonical list of scopes an API application can be granted. */
export const API_APP_SCOPES = [
  'company:lookup:pl',
  'company:lookup:eu',
  // Inbound leads from company websites / integrations (spec 2026-09-09).
  // Unlike the lookup scopes this one is company-bound: an application
  // granted it MUST carry companyNip, because the lead has to land in a
  // specific CRM. Enforced in ApiAppsTokenService.validate.
  'crm:leads:write',
] as const;
export type ApiAppScope = (typeof API_APP_SCOPES)[number];

/** Scopes that require the application to be bound to a company. */
export const COMPANY_BOUND_API_APP_SCOPES: readonly ApiAppScope[] = ['crm:leads:write'];

export interface ApiApplicationDto {
  id: string;
  name: string;
  description: string | null;
  tokenPrefix: string;
  scopes: string[];
  /** Company (NIP) this application writes to; null = global (lookup-only). */
  companyNip: string | null;
  /** ISO date-time or null = never expires. */
  expiresAt: string | null;
  active: boolean;
  lastUsedAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

// ==================== NIP helpers ====================

/** Mod-11 checksum weights for the first 9 digits of a Polish NIP. */
const POLISH_NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

/**
 * True when `value` is a checksum-valid Polish NIP: 10 digits, an optional
 * `PL` prefix and spaces/dashes tolerated. The ONE implementation for both
 * sides: the frontend decides (without a round-trip) whether a typed NIP is
 * worth a registry lookup, and the backend re-exports it from
 * `common/utils/nip.util.ts` — OCR routinely misreads arbitrary numbers
 * (order/customer ids) as VendorTaxId on foreign invoices, and the checksum
 * rejects ~10/11 of random values, so anything failing it is never a NIP.
 */
export function isValidPolishNip(value: string | null | undefined): boolean {
  if (!value) return false;
  const cleaned = value.replace(/[\s-]/g, '').toUpperCase();
  const digits = cleaned.startsWith('PL') ? cleaned.slice(2) : cleaned;
  if (!/^\d{10}$/.test(digits)) return false;
  const sum = POLISH_NIP_WEIGHTS.reduce((acc, w, i) => acc + w * Number(digits[i]), 0);
  const control = sum % 11;
  return control !== 10 && control === Number(digits[9]);
}

// ==================== CRM (spec 2026-09-09) ====================
// Jeden CRM FSS: firma-klient to rekord CrmAccount wewnątrz przestrzeni
// firmy operacyjnej (companyNip), nie osobny tenant.

export const CRM_ACCOUNT_STATUSES = ['lead', 'prospect', 'customer', 'inactive'] as const;
export type CrmAccountStatus = (typeof CRM_ACCOUNT_STATUSES)[number];

export const CRM_LEAD_STATUSES = ['new', 'assigned', 'converted', 'spam', 'rejected'] as const;
export type CrmLeadStatus = (typeof CRM_LEAD_STATUSES)[number];

/**
 * Why a salesperson hid a lead. `spam` maps to lead status `spam`, every
 * other code to status `rejected` — so status filters and form-abuse
 * detection keep working unchanged.
 */
export const CRM_LEAD_REJECT_REASONS = [
  'spam',
  'not_interested',
  'no_contact',
  'out_of_scope',
  'duplicate',
  'other',
] as const;
export type CrmLeadRejectReason = (typeof CRM_LEAD_REJECT_REASONS)[number];

/**
 * Role, które mogą wiązać obiekty CRM z rekordami po stronie księgowej
 * (kontrahent, projekt). Sam moduł `crm` na grancie `user` nie wystarczy:
 * te akcje piszą do kartoteki, nie do CRM-u. Jedno źródło prawdy dla bramki
 * w kontrolerze i dla ukrywania przycisków w UI.
 */
export const CRM_LINK_ROLES = ['crm', 'admin', 'ksiegowy'] as const;

/** Lead list filter: a concrete status, or `active` = new + assigned. */
export type CrmLeadStatusFilter = CrmLeadStatus | 'active';

/** Statuses the UI offers "hide" on (= the `active` queue). */
export const CRM_LEAD_HIDEABLE_STATUSES: readonly CrmLeadStatus[] = ['new', 'assigned'];
/** Statuses that mean "hidden" — restorable. */
export const CRM_LEAD_HIDDEN_STATUSES: readonly CrmLeadStatus[] = ['rejected', 'spam'];

/**
 * Hard cap on rows returned by the CRM contact list (`listContacts`) — the
 * frontend shows "pokazano pierwsze N" rather than paginating; kept here so
 * both sides quote the same number.
 */
export const CRM_CONTACT_MAX = 500;

export const CRM_TASK_STATUSES = ['open', 'done', 'cancelled'] as const;
export type CrmTaskStatus = (typeof CRM_TASK_STATUSES)[number];

export const CRM_ACTIVITY_TYPES = [
  'note',
  'call',
  'meeting',
  'email_out',
  'email_in',
  'stage_change',
  'lead_received',
  'lead_converted',
  'deal_created',
  'task_done',
  'file',
] as const;
export type CrmActivityType = (typeof CRM_ACTIVITY_TYPES)[number];

/** Default pipeline seeded on a company's first visit to the CRM module. */
export const CRM_DEFAULT_STAGES: ReadonlyArray<{
  key: string;
  name: string;
  probability: number;
  isWon?: boolean;
  isLost?: boolean;
}> = [
  { key: 'nowy', name: 'Nowy', probability: 10 },
  { key: 'kwalifikacja', name: 'Kwalifikacja', probability: 25 },
  { key: 'wycena', name: 'Wycena', probability: 40 },
  { key: 'oferta', name: 'Oferta', probability: 60 },
  { key: 'negocjacje', name: 'Negocjacje', probability: 80 },
  { key: 'wygrana', name: 'Wygrana', probability: 100, isWon: true },
  { key: 'przegrana', name: 'Przegrana', probability: 0, isLost: true },
];

export interface CrmAccount {
  id: string;
  companyNip: string;
  name: string;
  legalName: string | null;
  nip: string | null;
  vatId: string | null;
  country: string | null;
  website: string | null;
  emailDomains: string[];
  email: string | null;
  phone: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  industry: string | null;
  sizeBucket: string | null;
  status: CrmAccountStatus;
  sourceName: string | null;
  ownerUserId: string | null;
  ownerDisplayName: string | null;
  contractorId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrmAccountListItem
  extends Pick<
    CrmAccount,
    'id' | 'name' | 'nip' | 'status' | 'city' | 'website' | 'ownerUserId' | 'ownerDisplayName' | 'updatedAt'
  > {
  openDeals: number;
  /** Sum of valueNet across the account's open deals, in PLN, or null. */
  openValueNet: number | null;
  lastActivityAt: string | null;
}

export interface CrmAccountDetail extends CrmAccount {
  contacts: CrmContact[];
  deals: CrmDealListItem[];
  openTasks: CrmTask[];
  /** Powiązany kontrahent (kartoteka księgowa) albo null, gdy konta nikt nie powiązał. */
  contractor: CrmAccountContractorSummary | null;
}

/**
 * Kontrahent widziany z karty klienta CRM: tyle, ile trzeba, by pokazać
 * panel „Kontrahent" i dojść do jego karty. Wycinek `ContractorListItem`, bo
 * to ten sam rekord kartoteki — pola nazwane po CRM-owemu (`name`/`nip`/
 * `city`), bo handlowiec nie zna kolumn `vendor_*`. `nip` przychodzi w
 * postaci, w jakiej trzyma go kartoteka (polski z prefiksem `PL`).
 */
export type CrmAccountContractorSummary = Pick<
  ContractorListItem,
  'id' | 'name' | 'nip' | 'city' | 'vatStatus'
>;

/** Skąd wziął się kandydat: identyczny NIP/VAT UE albo zbieżna nazwa. */
export type CrmContractorMatchKind = 'exact' | 'name';

export interface CrmContractorCandidate extends CrmAccountContractorSummary {
  match: CrmContractorMatchKind;
  /**
   * Nazwa INNEGO konta CRM tej firmy, które już wskazuje na tego kontrahenta
   * — ostrzeżenie przed rozdwojeniem klienta; null, gdy kontrahent jest wolny.
   */
  linkedAccountName: string | null;
}

export interface CrmContractorCandidatesResponse {
  candidates: CrmContractorCandidate[];
}

/**
 * `created=false` oznacza, że kontrahent o tym samym NIP/VAT UE (albo — dla
 * bez-NIP-owych — o tej samej znormalizowanej nazwie) już istniał i został
 * powiązany zamiast zduplikowany. Zwracamy sam podpięty rekord kartoteki:
 * karta klienta i tak przeładowuje się po tej akcji, więc drugie pełne
 * pobranie konta nie miałoby czego dodać.
 */
export interface CrmCreateContractorResult {
  created: boolean;
  contractor: CrmAccountContractorSummary;
}

/** `contractorId: null` = odłączenie kontrahenta od konta. */
export interface LinkCrmContractorDto {
  contractorId: string | null;
}

export interface UpsertCrmAccountDto {
  name: string;
  legalName?: string | null;
  nip?: string | null;
  vatId?: string | null;
  country?: string | null;
  website?: string | null;
  emailDomains?: string[];
  email?: string | null;
  phone?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  industry?: string | null;
  sizeBucket?: string | null;
  status?: CrmAccountStatus;
  sourceName?: string | null;
  ownerUserId?: string | null;
  notes?: string | null;
}

export interface CrmContact {
  id: string;
  companyNip: string;
  accountId: string | null;
  accountName: string | null;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  isPrimary: boolean;
  marketingOptOut: boolean;
  ownerUserId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertCrmContactDto {
  accountId?: string | null;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
  isPrimary?: boolean;
  marketingOptOut?: boolean;
  ownerUserId?: string | null;
  notes?: string | null;
}

export interface CrmStage {
  id: string;
  companyNip: string;
  key: string;
  name: string;
  sortOrder: number;
  probability: number;
  isWon: boolean;
  isLost: boolean;
  active: boolean;
}

export interface CrmDealListItem {
  id: string;
  name: string;
  accountId: string;
  accountName: string;
  stageId: string;
  stageKey: string;
  stageName: string;
  valueNet: number | null;
  currency: string;
  probability: number | null;
  expectedCloseAt: string | null;
  closedAt: string | null;
  ownerUserId: string | null;
  ownerDisplayName: string | null;
  /** When the deal entered its current stage (board "days in stage"); createdAt for never-moved deals. */
  stageEnteredAt: string;
  /** Newest crm_activities.occurredAt for the deal, null when it has none (board "no contact" health). */
  lastActivityAt: string | null;
  updatedAt: string;
}

export interface CrmDealDetail extends CrmDealListItem {
  primaryContactId: string | null;
  sourceLeadId: string | null;
  lostReason: string | null;
  projectId: string | null;
  projectName: string | null;
  notes: string | null;
  createdAt: string;
}

export interface UpsertCrmDealDto {
  accountId: string;
  name: string;
  stageId?: string;
  primaryContactId?: string | null;
  valueNet?: number | null;
  currency?: string;
  probability?: number | null;
  expectedCloseAt?: string | null;
  ownerUserId?: string | null;
  notes?: string | null;
}

/**
 * PUT deals/:id body — every key optional; only the keys present are
 * patched (`onlySentKeys`). The deal sheet's inline fields send exactly the
 * one key that was edited, so two people editing different fields of the
 * same deal — or a concurrent rename — never clobber each other.
 */
export type PatchCrmDealDto = Partial<UpsertCrmDealDto>;

/** GET companies/:nip/crm/users — active holders of a deal-owning role in that company (owner pickers; see DEAL_OWNER_ROLES). */
export interface CrmUserOption {
  id: string;
  displayName: string;
  email: string;
}

export interface ChangeCrmDealStageDto {
  stageId: string;
  note?: string;
  /** Required when moving into a stage flagged isLost. */
  lostReason?: string;
}

export interface CrmLeadListItem {
  id: string;
  sourceName: string;
  formKey: string | null;
  status: CrmLeadStatus;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  messagePreview: string | null;
  accountId: string | null;
  accountName: string | null;
  ownerUserId: string | null;
  receivedAt: string;
  /** Set when status is `rejected`/`spam`; legacy rows may carry null. */
  rejectedReasonCode: CrmLeadRejectReason | null;
}

export interface CrmLeadDetail extends CrmLeadListItem {
  companyNip: string;
  apiApplicationId: string | null;
  sourceUrl: string | null;
  externalId: string | null;
  nip: string | null;
  message: string | null;
  consentMarketing: boolean | null;
  utm: Record<string, unknown> | null;
  /** Everything the sender posted, unmodified — the evidence copy. */
  payload: Record<string, unknown>;
  /** Optional free-text note accompanying `rejectedReasonCode`. */
  rejectedReason: string | null;
  contactId: string | null;
  dealId: string | null;
  processedAt: string | null;
  activities: CrmActivity[];
}

/**
 * POST leads/:id/reject. `reasonCode` is required; legacy body
 * `{spam?: boolean, reason?: string}` is still accepted server-side
 * (spam:true → `spam`, otherwise `other`).
 */
export interface RejectCrmLeadDto {
  reasonCode: CrmLeadRejectReason;
  note?: string | null;
}

/** POST leads/bulk-reject — one reason for every selected lead. */
export interface BulkRejectCrmLeadsDto extends RejectCrmLeadDto {
  ids: string[];
}

/** POST leads/bulk-delete (company admin / superadmin only) and POST leads/bulk-restore. */
export interface CrmLeadIdsDto {
  ids: string[];
}

/**
 * Every bulk lead operation: `affected` rows changed/deleted, `skipped` =
 * converted leads, ids not in this company, or ids in a status the
 * operation does not apply to.
 */
export interface BulkCrmLeadsResult {
  affected: number;
  skipped: number;
}

/** Manually entered lead (panel) — same shape as the public API minus auth. */
export interface CreateCrmLeadDto {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  nip?: string | null;
  message?: string | null;
  sourceName?: string | null;
  sourceUrl?: string | null;
  consentMarketing?: boolean | null;
  accountId?: string | null;
  ownerUserId?: string | null;
}

export interface ConvertCrmLeadDto {
  /** Existing account to attach to; omit to create one from `newAccountName`. */
  accountId?: string;
  newAccountName?: string;
  /** Create a contact from the lead's person fields (default true). */
  createContact?: boolean;
  /** Create a deal; omit the object to skip deal creation. */
  deal?: { name: string; stageId?: string; valueNet?: number | null; ownerUserId?: string | null };
}

export interface ConvertCrmLeadResult {
  leadId: string;
  accountId: string;
  contactId: string | null;
  dealId: string | null;
}

export interface CrmActivity {
  id: string;
  type: CrmActivityType;
  subject: string | null;
  body: string | null;
  payload: Record<string, unknown> | null;
  occurredAt: string;
  authorUserId: string | null;
  authorDisplayName: string | null;
  accountId: string | null;
  dealId: string | null;
  contactId: string | null;
  leadId: string | null;
}

export interface CreateCrmActivityDto {
  type: CrmActivityType;
  subject?: string | null;
  body?: string | null;
  occurredAt?: string;
  accountId?: string | null;
  dealId?: string | null;
  contactId?: string | null;
}

export interface CrmTask {
  id: string;
  title: string;
  description: string | null;
  dueAt: string | null;
  assigneeUserId: string;
  assigneeDisplayName: string | null;
  status: CrmTaskStatus;
  doneAt: string | null;
  accountId: string | null;
  accountName: string | null;
  dealId: string | null;
  createdAt: string;
}

export interface UpsertCrmTaskDto {
  title: string;
  description?: string | null;
  dueAt?: string | null;
  assigneeUserId: string;
  accountId?: string | null;
  dealId?: string | null;
  contactId?: string | null;
  status?: CrmTaskStatus;
}

export interface CrmAccountListParams {
  search?: string;
  status?: CrmAccountStatus;
  ownerUserId?: string;
  page?: number;
  pageSize?: number;
}

export interface CrmLeadListParams {
  search?: string;
  status?: CrmLeadStatusFilter;
  sourceName?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// ---- Public lead ingest (POST /public/v1/leads, pap_… token) ----

export interface PublicLeadPayload {
  formKey?: string;
  sourceUrl?: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  nip?: string;
  message?: string;
  consentMarketing?: boolean;
  utm?: Record<string, string>;
  /** Any extra form fields — preserved verbatim in the lead's payload. */
  custom?: Record<string, unknown>;
}

export interface PublicLeadResult {
  id: string;
  status: CrmLeadStatus;
  /** true = idempotent replay; nothing new was created. */
  duplicate: boolean;
}
